import { ChangeDetectionStrategy, Component, inject, signal, OnInit, OnDestroy, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthService } from './auth.service';
import { AccountStatus } from './models/auth.model';
import { NotificationService } from './notification';
import { Subject, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { collection, query, where, getDocs, doc, setDoc, serverTimestamp, limit } from 'firebase/firestore';
import { db, storage } from './firebase';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { AiService } from './services/ai.service';

type AuthMode = 'signin' | 'signup' | 'forgot' | 'verifyEmail' | 'resetPassword' | 'recoverEmail' | 'actionSuccess';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-auth',
  imports: [CommonModule, FormsModule, MatIconModule, RouterModule],
  template: `
@if (!authService.isAuthenticated() || mode() === 'actionSuccess' || mode() === 'verifyEmail' || mode() === 'resetPassword' || mode() === 'recoverEmail') {
<div class="min-h-screen w-full flex items-center justify-center p-4 md:p-6 transition-colors duration-500 relative overflow-x-hidden">
  <div class="w-full max-w-[460px] glass-card p-6 md:p-12 animate-ios-slide relative z-10">

    <!-- Logo & Header -->
    <div class="flex flex-col items-center mb-10">
      <div class="cursor-pointer active:scale-90 transition-all duration-500 group mb-6" (click)="onLogoClick()" (keydown.enter)="onLogoClick()" tabindex="0" aria-label="Kicgle Home">
        <div class="relative">
          <div class="absolute inset-0 bg-ios-blue/30 blur-3xl rounded-full scale-150 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
          <img src="kicgle.svg" alt="Kicgle" class="h-20 w-auto relative z-10 drop-shadow-[0_10px_20px_rgba(0,122,255,0.3)] dark:drop-shadow-[0_10px_20px_rgba(0,122,255,0.5)]" referrerpolicy="no-referrer">
        </div>
      </div>
      
      <div class="text-center">
        <h1 class="text-ios-title text-4xl font-extrabold tracking-tight mb-3 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-600 dark:from-white dark:via-slate-100 dark:to-slate-400 bg-clip-text text-transparent">
          @switch (mode()) {
            @case ('signin') { Welcome Back }
            @case ('signup') { Create Account }
            @case ('forgot') { Reset Password }
            @case ('verifyEmail') { Verifying Email }
            @case ('resetPassword') { New Password }
            @case ('recoverEmail') { Recovering Email }
            @case ('actionSuccess') { Success! }
          }
        </h1>
        <p class="text-ios-caption text-[16px] font-medium opacity-70">
          @switch (mode()) {
            @case ('signin') { Sign in to access your Kicgle world }
            @case ('signup') { Join our community of explorers }
            @case ('forgot') { We'll help you recover your account }
            @case ('verifyEmail') { We are confirming your email address }
            @case ('resetPassword') { Enter your new password below }
            @case ('recoverEmail') { Restoring your previous email address }
            @case ('actionSuccess') { Your account has been updated }
          }
        </p>
      </div>
    </div>

    <!-- Form -->
    @if (!authService.isSigningUp() && (mode() === 'signin' || mode() === 'signup' || mode() === 'forgot' || mode() === 'resetPassword')) {
    <form (submit)="onSubmit()" class="space-y-6">
      
      <!-- Step Indicator -->
      @if (mode() === 'signup') {
        <div class="mb-10 px-2 animate-ios-fade">
          <div class="flex items-center justify-between mb-4">
            <div class="flex flex-col gap-1">
              <span class="text-[11px] font-black uppercase tracking-[0.25em] text-ios-blue dark:text-ios-blue-dark opacity-60">Step {{ currentStep() }} of 3</span>
              <h2 class="text-[22px] font-black text-slate-900 dark:text-white tracking-tight">
                @if (currentStep() === 1) { Identity }
                @else if (currentStep() === 2) { 
                  {{ accountType() === 'person' ? 'Face Verification' : 'Authorized Persons' }}
                }
                @else { Account Details }
              </h2>
            </div>
            <div class="w-12 h-12 rounded-2xl bg-ios-blue/10 dark:bg-ios-blue-dark/10 flex items-center justify-center">
              <mat-icon class="text-ios-blue dark:text-ios-blue-dark text-[24px]">
                @if (currentStep() === 1) { fingerprint }
                @else if (currentStep() === 2) { 
                  {{ accountType() === 'person' ? 'face' : 'group' }}
                }
                @else { person_add }
              </mat-icon>
            </div>
          </div>
          <div class="flex gap-2">
            @for (step of [1, 2, 3]; track step) {
              <div class="flex-1 h-2 rounded-full overflow-hidden bg-white/5 border border-white/5">
                <div 
                  class="h-full bg-ios-blue transition-all duration-700 ease-ios-out"
                  [style.width.%]="currentStep() >= step ? 100 : 0"
                  [class.opacity-40]="currentStep() > step"
                ></div>
              </div>
            }
          </div>
        </div>
      }
      
      <!-- PAN Upload (Only for Signup) -->
      @if (mode() === 'signup') {
        <div class="space-y-4 animate-ios-slide">
          <div class="flex items-center justify-between px-1">
            <p class="text-[13px] font-bold uppercase tracking-widest text-ios-blue/80 dark:text-ios-blue-dark/80">Identity Verification</p>
            @if (panVerified()) {
              <span class="flex items-center gap-1 text-[11px] font-bold text-green-500 bg-green-500/10 px-2 py-0.5 rounded-full">
                <mat-icon class="text-[14px] h-[14px] w-[14px]">verified</mat-icon> Verified
              </span>
            }
          </div>

          @if (!panImage()) {
            <!-- Selection Option between Upload PAN and Take PAN -->
            <div class="grid grid-cols-2 p-1 bg-black/5 dark:bg-white/5 rounded-2xl border border-black/5 dark:border-white/10 mb-3">
              <button
                type="button"
                (click)="setPanInputMethod('upload')"
                class="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs font-bold transition-all duration-300"
                [class.bg-white]="panInputMethod() === 'upload'"
                [class.dark:bg-white/15]="panInputMethod() === 'upload'"
                [class.shadow-md]="panInputMethod() === 'upload'"
                [class.text-ios-blue]="panInputMethod() === 'upload'"
                [class.dark:text-ios-blue-dark]="panInputMethod() === 'upload'"
                [class.text-ios-caption]="panInputMethod() !== 'upload'"
                [class.opacity-60]="panInputMethod() !== 'upload'"
              >
                <mat-icon class="text-[18px] w-[18px] h-[18px]">upload_file</mat-icon>
                <span>Upload PAN</span>
              </button>
              <button
                type="button"
                (click)="setPanInputMethod('camera')"
                class="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs font-bold transition-all duration-300"
                [class.bg-white]="panInputMethod() === 'camera'"
                [class.dark:bg-white/15]="panInputMethod() === 'camera'"
                [class.shadow-md]="panInputMethod() === 'camera'"
                [class.text-ios-blue]="panInputMethod() === 'camera'"
                [class.dark:text-ios-blue-dark]="panInputMethod() === 'camera'"
                [class.text-ios-caption]="panInputMethod() !== 'camera'"
                [class.opacity-60]="panInputMethod() !== 'camera'"
              >
                <mat-icon class="text-[18px] w-[18px] h-[18px]">photo_camera</mat-icon>
                <span>Take PAN</span>
              </button>
            </div>

            <!-- Validation Error Alert Banner -->
            @if (panValidationError()) {
              <div class="p-3.5 mb-3 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 flex items-start gap-3 animate-ios-fade">
                <mat-icon class="text-amber-500 text-xl shrink-0 mt-0.5">warning</mat-icon>
                <div class="flex-1 text-xs">
                  <p class="font-bold text-amber-800 dark:text-amber-200 mb-0.5">Invalid Document or Object Detected</p>
                  <p class="opacity-90 leading-relaxed">{{ panValidationError() }}</p>
                  <div class="mt-2.5 flex gap-2">
                    <button 
                      type="button" 
                      (click)="panValidationError.set(null); setPanInputMethod('camera')"
                      class="px-3 py-1.5 rounded-lg bg-amber-500 text-white font-bold text-[11px] shadow hover:bg-amber-600 transition-colors flex items-center gap-1.5 cursor-pointer"
                    >
                      <mat-icon class="text-[14px] w-[14px] h-[14px]">photo_camera</mat-icon>
                      Reposition & Retake
                    </button>
                    <button 
                      type="button" 
                      (click)="panValidationError.set(null); setPanInputMethod('upload')"
                      class="px-3 py-1.5 rounded-lg bg-black/10 dark:bg-white/10 font-semibold text-[11px] hover:bg-black/20 dark:hover:bg-white/20 transition-colors flex items-center gap-1.5 cursor-pointer"
                    >
                      <mat-icon class="text-[14px] w-[14px] h-[14px]">upload_file</mat-icon>
                      Upload File
                    </button>
                  </div>
                </div>
                <button type="button" (click)="panValidationError.set(null)" class="text-amber-500/70 hover:text-amber-500 cursor-pointer">
                  <mat-icon class="text-lg">close</mat-icon>
                </button>
              </div>
            }

            <!-- View 1: Upload PAN -->
            @if (panInputMethod() === 'upload') {
              <div 
                (click)="panInput.click()" 
                (keydown.enter)="panInput.click()"
                tabindex="0"
                class="relative group cursor-pointer animate-ios-fade"
              >
                <div class="absolute -inset-1 bg-gradient-to-r from-ios-blue/20 to-ios-purple/20 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                <div class="relative flex flex-col items-center justify-center p-8 border-2 border-dashed border-ios-blue/20 rounded-2xl bg-white/5 hover:bg-white/10 hover:border-ios-blue/40 transition-all duration-500">
                  <div class="w-16 h-16 bg-ios-blue/10 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-500">
                    <mat-icon class="text-ios-blue text-3xl">upload_file</mat-icon>
                  </div>
                  <p class="text-[15px] font-bold text-ios-title mb-1">Upload PAN</p>
                  <p class="text-[13px] text-ios-caption opacity-60">JPEG, PNG or WEBP from device</p>
                  <input type="file" (change)="onPanUpload($event)" accept="image/*" class="hidden" id="pan-input" #panInput>
                </div>
              </div>
            }

            <!-- View 2: Take PAN (HTML5 Camera with Scanner Frame Overlay) -->
            @if (panInputMethod() === 'camera') {
              <div class="relative w-full min-h-[340px] aspect-[4/3] sm:aspect-[16/10] max-h-[440px] rounded-2xl overflow-hidden bg-black shadow-2xl border border-white/10 animate-ios-fade flex flex-col justify-between">
                <!-- Video Stream Element -->
                <video 
                  id="pan-card-video" 
                  autoplay 
                  playsinline 
                  muted 
                  class="absolute inset-0 w-full h-full object-cover z-0"
                ></video>

                <!-- Camera Loading State -->
                @if (panCameraLoading()) {
                  <div class="absolute inset-0 bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center gap-3 z-30">
                    <div class="w-10 h-10 border-3 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin"></div>
                    <p class="text-white text-xs font-semibold">Starting camera...</p>
                  </div>
                }

                <!-- Camera Error State -->
                @if (panCameraError()) {
                  <div class="absolute inset-0 bg-black/90 backdrop-blur-md flex flex-col items-center justify-center p-6 text-center gap-3 z-30">
                    <div class="w-12 h-12 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center">
                      <mat-icon class="text-2xl">videocam_off</mat-icon>
                    </div>
                    <p class="text-white text-sm font-bold">Camera Access Error</p>
                    <p class="text-white/70 text-xs max-w-[280px]">{{ panCameraError() }}</p>
                    <div class="flex gap-2 pt-2">
                      <button 
                        type="button" 
                        (click)="startPanCamera()" 
                        class="px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white text-xs font-semibold transition-colors flex items-center gap-1.5"
                      >
                        <mat-icon class="text-[14px] w-[14px] h-[14px]">refresh</mat-icon> Retry
                      </button>
                      <button 
                        type="button" 
                        (click)="setPanInputMethod('upload')" 
                        class="px-3 py-1.5 rounded-lg bg-ios-blue text-white text-xs font-semibold transition-colors flex items-center gap-1.5 shadow"
                      >
                        <mat-icon class="text-[14px] w-[14px] h-[14px]">upload_file</mat-icon> Upload File
                      </button>
                    </div>
                  </div>
                }

                <!-- Viewfinder & Frame Overlay Layer -->
                @if (isPanCameraActive() && !panCameraLoading() && !panCameraError()) {
                  <div class="relative w-full h-full flex flex-col justify-between z-20 pointer-events-none">
                    
                    <!-- Top Bar with Guidance -->
                    <div class="w-full flex items-center justify-between p-2 px-3 bg-gradient-to-b from-black/80 via-black/40 to-transparent pointer-events-auto z-30 relative shrink-0">
                      <span class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-[10px] font-bold text-white/90">
                        <span class="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                        PAN SCANNER
                      </span>
                      <button 
                        type="button" 
                        (click)="togglePanFacingMode()" 
                        class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-black/60 backdrop-blur-md hover:bg-black/80 border border-white/15 text-[10px] font-bold text-white transition-colors cursor-pointer"
                        title="Flip Camera"
                      >
                        <mat-icon class="text-[13px] w-[13px] h-[13px]">flip_camera_ios</mat-icon>
                        Flip
                      </button>
                    </div>

                    <!-- Centered PAN Card Scanner Frame with clear boundary (Increased size & compact vertical margins) -->
                    <div class="relative w-[92%] sm:w-[88%] max-w-[340px] aspect-[1.586/1] rounded-xl flex flex-col justify-between p-2.5 border border-white/40 border-dashed my-auto mx-auto z-10 shrink-0 shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]">
                      
                      <!-- 4 High-Precision Frame Corner Brackets -->
                      <div class="absolute -top-[2px] -left-[2px] w-6 h-6 border-t-3 border-l-3 border-ios-blue rounded-tl-lg shadow-[0_0_8px_rgba(0,122,255,0.6)]"></div>
                      <div class="absolute -top-[2px] -right-[2px] w-6 h-6 border-t-3 border-r-3 border-ios-blue rounded-tr-lg shadow-[0_0_8px_rgba(0,122,255,0.6)]"></div>
                      <div class="absolute -bottom-[2px] -left-[2px] w-6 h-6 border-b-3 border-l-3 border-ios-blue rounded-bl-lg shadow-[0_0_8px_rgba(0,122,255,0.6)]"></div>
                      <div class="absolute -bottom-[2px] -right-[2px] w-6 h-6 border-b-3 border-r-3 border-ios-blue rounded-br-lg shadow-[0_0_8px_rgba(0,122,255,0.6)]"></div>

                      <!-- Subtle Animated Laser Scan Line -->
                      <div class="absolute inset-x-2 h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent shadow-[0_0_10px_#22d3ee] animate-pulse"></div>

                      <!-- PAN Card Watermark Guides -->
                      <div class="flex items-center justify-between opacity-80 pointer-events-none">
                        <span class="text-[8px] font-black uppercase tracking-wider text-white/90 bg-black/40 px-1.5 py-0.5 rounded">INCOME TAX DEPARTMENT</span>
                        <span class="text-[8px] font-bold text-white/70">GOVT. OF INDIA</span>
                      </div>

                      <!-- Alignment silhouette interior -->
                      <div class="flex items-center justify-between my-auto opacity-30 pointer-events-none px-1">
                        <div class="w-7 h-9 border border-dashed border-white rounded flex items-center justify-center">
                          <mat-icon class="text-white text-[13px]">person</mat-icon>
                        </div>
                        <div class="flex-1 mx-2 space-y-1">
                          <div class="h-1 w-3/4 bg-white rounded"></div>
                          <div class="h-1 w-1/2 bg-white rounded"></div>
                          <div class="h-1 w-2/3 bg-white rounded"></div>
                        </div>
                        <div class="w-9 h-4 border border-dashed border-white rounded flex items-center justify-center">
                          <mat-icon class="text-white text-[9px]">edit</mat-icon>
                        </div>
                      </div>

                      <!-- Bottom helper badge -->
                      <div class="text-center pointer-events-none">
                        <span class="text-[9px] font-semibold text-white/90 bg-black/60 px-2 py-0.5 rounded-full backdrop-blur-sm">
                          Align all 4 corners of PAN card
                        </span>
                      </div>
                    </div>

                    <!-- Bottom Controls: Return to Upload, Round Shutter Button, Flip Camera -->
                    <div class="w-full flex items-center justify-between px-4 py-2 bg-gradient-to-t from-black/90 via-black/60 to-transparent pointer-events-auto z-30 relative shrink-0">
                      <button 
                        type="button" 
                        (click)="setPanInputMethod('upload')" 
                        class="w-10 h-10 rounded-full bg-black/60 backdrop-blur-md text-white/80 hover:text-white flex items-center justify-center border border-white/20 transition-all active:scale-95 shadow-lg cursor-pointer"
                        title="Switch to upload file"
                      >
                        <mat-icon class="text-[20px]">upload_file</mat-icon>
                      </button>

                      <!-- Round Capture Shutter Button -->
                      <button 
                        type="button" 
                        (click)="capturePanPhoto()" 
                        [disabled]="!isPanCameraActive() || panCameraLoading()"
                        class="w-14 h-14 rounded-full border-4 border-white p-0.5 flex items-center justify-center active:scale-90 transition-transform shadow-[0_0_20px_rgba(0,0,0,0.8)] hover:scale-105 disabled:opacity-50 group cursor-pointer z-30"
                        title="Capture PAN Card"
                      >
                        <div class="w-full h-full bg-white rounded-full flex items-center justify-center text-ios-blue shadow group-hover:bg-slate-100 transition-colors">
                          <mat-icon class="text-[24px]">camera_alt</mat-icon>
                        </div>
                      </button>

                      <button 
                        type="button" 
                        (click)="togglePanFacingMode()" 
                        class="w-10 h-10 rounded-full bg-black/60 backdrop-blur-md text-white/80 hover:text-white flex items-center justify-center border border-white/20 transition-all active:scale-95 shadow-lg cursor-pointer"
                        title="Flip Camera"
                      >
                        <mat-icon class="text-[20px]">flip_camera_ios</mat-icon>
                      </button>
                    </div>

                  </div>
                }
              </div>
            }
          } @else if (loadingPan()) {
            <div class="relative aspect-[1.6/1] rounded-2xl overflow-hidden border border-white/10 shadow-2xl animate-ios-fade">
              <img [src]="panImage()" alt="PAN Preview" class="w-full h-full object-cover">
              <div class="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center gap-4">
                <div class="w-12 h-12 border-4 border-ios-blue/20 border-t-ios-blue rounded-full animate-spin"></div>
                <p class="text-white text-sm font-bold animate-pulse">Extracting Identity...</p>
              </div>
            </div>
          }

          @if (panDetails()) {
            <div class="bg-gradient-to-br from-white/10 to-white/5 dark:from-black/20 dark:to-black/10 p-5 rounded-2xl border border-white/10 space-y-4 shadow-xl animate-ios-slide">
              <div class="flex justify-between items-start">
                <div class="space-y-1">
                  <p class="text-[10px] font-bold uppercase tracking-widest opacity-40">Extracted Name</p>
                  <p class="text-[16px] font-bold text-ios-title">{{ panDetails()?.name }}</p>
                </div>
                @if (!panVerified()) {
                  <button type="button" (click)="removePanImage()" class="w-10 h-10 bg-ios-blue/10 rounded-lg flex items-center justify-center hover:bg-red-500/10 hover:text-red-500 transition-colors group" title="Re-upload PAN">
                    <mat-icon class="text-ios-blue group-hover:text-red-500">refresh</mat-icon>
                  </button>
                }
              </div>
              
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-1">
                  <p class="text-[10px] font-bold uppercase tracking-widest opacity-40">Date of Birth</p>
                  <p class="text-[14px] font-semibold">{{ panDetails()?.dob }}</p>
                </div>
                <div class="space-y-1">
                  <p class="text-[10px] font-bold uppercase tracking-widest opacity-40">PAN Number</p>
                  <p class="text-[14px] font-mono font-bold tracking-wider">{{ panDetails()?.panNumber }}</p>
                </div>
              </div>

              <div class="pt-2 border-t border-white/5 flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <div class="w-2 h-2 rounded-full bg-ios-blue animate-pulse"></div>
                  <p class="text-[11px] font-bold uppercase tracking-tighter opacity-60">{{ accountType() }} Account</p>
                </div>
                @if (panVerified()) {
                  <mat-icon class="text-green-500 text-[18px]">verified_user</mat-icon>
                }
              </div>
            </div>

            @if (existingPanAccount()) {
              <div class="bg-red-500/10 border border-red-500/30 p-4 rounded-2xl flex items-center gap-4 animate-ios-slide">
                <div class="relative">
                  @if (existingPanAccount()?.photo) {
                    <img [src]="existingPanAccount()?.photo" alt="Account Photo" class="w-14 h-14 rounded-full object-cover border-2 border-red-500/50">
                  } @else {
                    <div class="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center border-2 border-red-500/50">
                      <mat-icon class="text-red-500 text-2xl">person</mat-icon>
                    </div>
                  }
                  <div class="absolute -bottom-1 -right-1 bg-red-500 text-white rounded-full p-0.5 shadow-lg">
                    <mat-icon class="text-[12px] h-[12px] w-[12px]">error</mat-icon>
                  </div>
                </div>
                <div>
                  <p class="text-[13px] font-bold text-red-500">PAN Already Registered</p>
                  <p class="text-[14px] font-medium">{{ existingPanAccount()?.name }}</p>
                  <p class="text-xs opacity-60">&#64;{{ existingPanAccount()?.username }}</p>
                </div>
              </div>
            }

            <!-- Selfie Section (Only for Person Accounts) -->
            @if (panVerified() && accountType() === 'person' && !existingPanAccount()) {
              <div class="space-y-4 animate-ios-slide">
                <div class="flex items-center justify-between px-1">
                  <p class="text-[13px] font-bold uppercase tracking-widest text-ios-blue/80 dark:text-ios-blue-dark/80">Face Verification</p>
                  @if (selfieImage()) {
                    <span class="flex items-center gap-1 text-[11px] font-bold text-green-500 bg-green-500/10 px-2 py-0.5 rounded-full">
                      <mat-icon class="text-[14px] h-[14px] w-[14px]">verified</mat-icon> Verified
                    </span>
                  }
                </div>

                @if (!selfieImage() && !isCameraActive()) {
                  <div 
                    (click)="startCamera()" 
                    (keydown.enter)="startCamera()"
                    tabindex="0"
                    class="relative group cursor-pointer"
                  >
                    <div class="absolute -inset-1 bg-gradient-to-r from-ios-blue/20 to-ios-purple/20 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                    <div class="relative flex flex-col items-center justify-center p-8 border-2 border-dashed border-ios-blue/20 rounded-2xl bg-white/5 hover:bg-white/10 hover:border-ios-blue/40 transition-all duration-500">
                      <div class="w-16 h-16 bg-ios-blue/10 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-500">
                        <mat-icon class="text-ios-blue text-3xl">face</mat-icon>
                      </div>
                      <p class="text-[15px] font-bold text-ios-title mb-1">Start Face Verification</p>
                      <p class="text-[12px] text-ios-caption opacity-60">Required for identity proof</p>
                    </div>
                  </div>
                } @else if (isCameraActive() || capturedPhotoTemp() || isValidatingPhoto()) {
                  <div class="space-y-4 animate-ios-fade">
                    <!-- Top Status Bar with Close / Reset Button -->
                    <div class="flex items-center justify-between px-1">
                      <div class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/5 dark:bg-white/10 text-xs font-bold text-ios-title">
                        @if (isValidatingPhoto()) {
                          <span class="w-2 h-2 rounded-full bg-ios-blue animate-ping"></span>
                          <span class="tracking-wider uppercase text-ios-blue dark:text-ios-blue-dark">Validating Captured Photo...</span>
                        } @else if (selfieValidationError()) {
                          <span class="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                          <span class="tracking-wider uppercase text-red-500">Validation Failed</span>
                        } @else {
                          <span class="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                          <span class="tracking-wider uppercase text-slate-800 dark:text-white">Live Face Scanner</span>
                        }
                      </div>
                      <button 
                        type="button" 
                        (click)="stopCamera()" 
                        class="w-8 h-8 rounded-full bg-black/5 dark:bg-white/10 hover:bg-red-500/20 text-slate-600 dark:text-white hover:text-red-500 transition-colors flex items-center justify-center cursor-pointer"
                        title="Close Camera"
                      >
                        <mat-icon class="text-sm">close</mat-icon>
                      </button>
                    </div>

                    <!-- Larger Circular Camera Feed Viewport with Capture Button Inside -->
                    <div 
                      class="relative w-64 h-64 sm:w-72 sm:h-72 mx-auto rounded-full overflow-hidden border-4 shadow-2xl bg-black flex items-center justify-center group transition-all duration-300"
                      [class.border-ios-blue/40]="!selfieValidationError() && !allChecksPassed()"
                      [class.border-emerald-400]="allChecksPassed() && !selfieValidationError() && !capturedPhotoTemp()"
                      [class.border-red-500]="!!selfieValidationError()"
                      [class.shadow-red-500/20]="!!selfieValidationError()"
                    >
                      @if (capturedPhotoTemp()) {
                        <!-- Freeze Captured Snapshot Frame -->
                        <img [src]="capturedPhotoTemp()" alt="Captured Photo" class="w-full h-full object-cover scale-x-[-1] absolute inset-0 z-0">
                      } @else {
                        <!-- Live Video Stream -->
                        <video id="selfie-video" autoplay playsinline muted class="w-full h-full object-cover scale-x-[-1] absolute inset-0 z-0"></video>
                      }

                      <!-- Overlay Scanner Ring & Success Indicator -->
                      @if (!capturedPhotoTemp()) {
                        <div class="absolute inset-0 rounded-full border-2 border-dashed border-white/40 animate-pulse pointer-events-none z-10"
                             [class.!border-emerald-400]="allChecksPassed()"></div>
                        
                        @if (allChecksPassed()) {
                          <div class="absolute inset-x-0 top-1/3 -translate-y-1/2 flex justify-center z-20 pointer-events-none animate-ios-bounce">
                            <div class="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-lg border-2 border-white/30">
                              <mat-icon class="text-2xl">verified</mat-icon>
                            </div>
                          </div>
                        }
                      }

                      <!-- Loading State Overlay on Photo Capture -->
                      @if (isValidatingPhoto()) {
                        <div class="absolute inset-0 z-30 bg-black/60 backdrop-blur-md flex flex-col items-center justify-center p-4 text-center animate-ios-fade">
                          <div class="w-12 h-12 border-3 border-white/20 border-t-white rounded-full animate-spin mb-3"></div>
                          <p class="text-xs font-black text-white tracking-wide">Validating Photo...</p>
                          <p class="text-[10px] text-white/80 mt-1 max-w-[180px] font-medium leading-tight">Verifying clear human face and checking all 4 criteria</p>
                        </div>
                      }

                      <!-- Error Badge Overlay when Validation Fails -->
                      @if (selfieValidationError()) {
                        <div class="absolute inset-0 z-20 bg-red-950/40 backdrop-blur-[2px] flex items-center justify-center pointer-events-none">
                          <div class="w-14 h-14 rounded-full bg-red-500/90 text-white flex items-center justify-center shadow-2xl border-2 border-white/50 animate-ios-bounce">
                            <mat-icon class="text-3xl">error</mat-icon>
                          </div>
                        </div>
                      }

                      <!-- Capture Button Inside Circular Frame -->
                      <div class="absolute bottom-3.5 inset-x-0 z-30 flex justify-center items-center pointer-events-auto px-4">
                        @if (isValidatingPhoto()) {
                          <div class="px-3.5 py-1.5 rounded-full bg-black/70 backdrop-blur-md text-white text-[10px] font-bold border border-white/20 flex items-center gap-1.5 shadow-lg">
                            <div class="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            <span>Validating...</span>
                          </div>
                        } @else if (selfieValidationError()) {
                          <button 
                            type="button" 
                            (click)="retakeSelfie()" 
                            class="px-4 py-2 rounded-full bg-red-600 hover:bg-red-700 text-white font-extrabold text-xs shadow-2xl flex items-center gap-1.5 active:scale-95 transition-all duration-300 backdrop-blur-md cursor-pointer border border-white/30 animate-ios-bounce"
                            title="Retake Photo"
                          >
                            <mat-icon class="text-base">restart_alt</mat-icon>
                            <span>Retake Photo</span>
                          </button>
                        } @else if (allChecksPassed()) {
                          <button 
                            type="button" 
                            (click)="captureSelfie()" 
                            class="px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-extrabold text-xs shadow-2xl flex items-center gap-1.5 active:scale-95 transition-all duration-300 backdrop-blur-md cursor-pointer border border-white/30 animate-ios-bounce"
                            title="Capture Photo"
                          >
                            <mat-icon class="text-base">photo_camera</mat-icon>
                            <span>Capture Photo</span>
                          </button>
                        } @else {
                          <div class="px-3.5 py-1.5 rounded-full bg-black/60 backdrop-blur-md text-white/80 text-[10px] font-bold border border-white/15 flex items-center gap-1.5 shadow-lg">
                            <span class="w-2 h-2 rounded-full bg-amber-400 animate-ping"></span>
                            <span>Verifying Face ({{ passedChecksCount() }}/4)...</span>
                          </div>
                        }
                      </div>
                    </div>

                    <!-- Prominent Error Message Card & Retake Prompt when Validation Fails -->
                    @if (selfieValidationError()) {
                      <div class="p-3.5 bg-red-500/10 dark:bg-red-500/15 border border-red-500/30 rounded-2xl flex flex-col items-center gap-2.5 text-center animate-ios-slide">
                        <div class="flex items-center gap-2 text-red-600 dark:text-red-400 font-bold text-xs">
                          <mat-icon class="text-lg shrink-0">error_outline</mat-icon>
                          <span>{{ selfieValidationError() }}</span>
                        </div>
                        <p class="text-[11px] text-slate-600 dark:text-zinc-300">
                          Please ensure a clear human face is present, good lighting, no cap/mask, and clothing is worn.
                        </p>
                        <button 
                          type="button" 
                          (click)="retakeSelfie()" 
                          class="px-5 py-2 rounded-full bg-ios-blue hover:bg-ios-blue-hover text-white text-xs font-black shadow-lg flex items-center gap-1.5 active:scale-95 transition-all cursor-pointer"
                        >
                          <mat-icon class="text-sm">restart_alt</mat-icon>
                          <span>Retake Photo</span>
                        </button>
                      </div>
                    }

                    <!-- Verification Progress Checklist (Single-Row 4-Column Grid View) -->
                    <div class="bg-black/5 dark:bg-white/5 p-3 rounded-2xl border border-black/5 dark:border-white/10 space-y-2">
                      <div class="text-[11px] font-black uppercase tracking-widest text-ios-blue dark:text-ios-blue-dark flex items-center justify-between px-1">
                        <span class="flex items-center gap-1 text-slate-900 dark:text-white font-extrabold">
                          <mat-icon class="text-ios-blue text-xs">verified_user</mat-icon>
                          Stages
                        </span>
                        <span class="px-2 py-0.5 rounded-full text-[9px] font-bold bg-ios-blue/10 text-ios-blue dark:text-ios-blue-dark border border-ios-blue/20">
                          {{ passedChecksCount() }}/4 Passed
                        </span>
                      </div>

                      <!-- 4 Sequential Verification Steps in a Single Row -->
                      <div class="grid grid-cols-4 gap-1.5 sm:gap-2">
                        <!-- Step 1: Lightning / Clear Lighting -->
                        <div 
                          class="flex flex-col items-center justify-center p-2 rounded-xl text-center transition-all duration-300 min-w-0"
                          [class.bg-emerald-500/15]="selfieChecklist().clearLighting"
                          [class.border]="selfieChecklist().clearLighting"
                          [class.border-emerald-500/30]="selfieChecklist().clearLighting"
                          [class.bg-white/50]="!selfieChecklist().clearLighting"
                          [class.dark:bg-white/5]="!selfieChecklist().clearLighting"
                        >
                          <div class="w-5 h-5 rounded-full flex items-center justify-center mb-1 shrink-0"
                               [class.bg-emerald-500]="selfieChecklist().clearLighting"
                               [class.text-white]="selfieChecklist().clearLighting"
                               [class.bg-black/10]="!selfieChecklist().clearLighting"
                               [class.dark:bg-white/10]="!selfieChecklist().clearLighting"
                               [class.text-slate-400]="!selfieChecklist().clearLighting">
                            <mat-icon class="text-[13px] h-[13px] w-[13px]">
                              {{ selfieChecklist().clearLighting ? 'check' : 'light_mode' }}
                            </mat-icon>
                          </div>
                          <p class="text-[10px] sm:text-[11px] font-bold text-slate-900 dark:text-white truncate w-full">Lightning</p>
                          <span class="text-[8px] font-extrabold uppercase mt-0.5"
                                [class.text-emerald-600]="selfieChecklist().clearLighting"
                                [class.dark:text-emerald-400]="selfieChecklist().clearLighting"
                                [class.text-slate-400]="!selfieChecklist().clearLighting">
                            {{ selfieChecklist().clearLighting ? 'Pass' : 'Check' }}
                          </span>
                        </div>

                        <!-- Step 2: Face Alignment & Live Check -->
                        <div 
                          class="flex flex-col items-center justify-center p-2 rounded-xl text-center transition-all duration-300 min-w-0"
                          [class.bg-emerald-500/15]="selfieChecklist().faceStraightAndLive"
                          [class.border]="selfieChecklist().faceStraightAndLive"
                          [class.border-emerald-500/30]="selfieChecklist().faceStraightAndLive"
                          [class.bg-white/50]="!selfieChecklist().faceStraightAndLive"
                          [class.dark:bg-white/5]="!selfieChecklist().faceStraightAndLive"
                        >
                          <div class="w-5 h-5 rounded-full flex items-center justify-center mb-1 shrink-0"
                               [class.bg-emerald-500]="selfieChecklist().faceStraightAndLive"
                               [class.text-white]="selfieChecklist().faceStraightAndLive"
                               [class.bg-black/10]="!selfieChecklist().faceStraightAndLive"
                               [class.dark:bg-white/10]="!selfieChecklist().faceStraightAndLive"
                               [class.text-slate-400]="!selfieChecklist().faceStraightAndLive">
                            <mat-icon class="text-[13px] h-[13px] w-[13px]">
                              {{ selfieChecklist().faceStraightAndLive ? 'check' : 'face' }}
                            </mat-icon>
                          </div>
                          <p class="text-[10px] sm:text-[11px] font-bold text-slate-900 dark:text-white truncate w-full">Face Align</p>
                          <span class="text-[8px] font-extrabold uppercase mt-0.5"
                                [class.text-emerald-600]="selfieChecklist().faceStraightAndLive"
                                [class.dark:text-emerald-400]="selfieChecklist().faceStraightAndLive"
                                [class.text-slate-400]="!selfieChecklist().faceStraightAndLive">
                            {{ selfieChecklist().faceStraightAndLive ? 'Pass' : 'Check' }}
                          </span>
                        </div>

                        <!-- Step 3: No Covering on the Face -->
                        <div 
                          class="flex flex-col items-center justify-center p-2 rounded-xl text-center transition-all duration-300 min-w-0"
                          [class.bg-emerald-500/15]="selfieChecklist().noCapNoMask"
                          [class.border]="selfieChecklist().noCapNoMask"
                          [class.border-emerald-500/30]="selfieChecklist().noCapNoMask"
                          [class.bg-white/50]="!selfieChecklist().noCapNoMask"
                          [class.dark:bg-white/5]="!selfieChecklist().noCapNoMask"
                        >
                          <div class="w-5 h-5 rounded-full flex items-center justify-center mb-1 shrink-0"
                               [class.bg-emerald-500]="selfieChecklist().noCapNoMask"
                               [class.text-white]="selfieChecklist().noCapNoMask"
                               [class.bg-black/10]="!selfieChecklist().noCapNoMask"
                               [class.dark:bg-white/10]="!selfieChecklist().noCapNoMask"
                               [class.text-slate-400]="!selfieChecklist().noCapNoMask">
                            <mat-icon class="text-[13px] h-[13px] w-[13px]">
                              {{ selfieChecklist().noCapNoMask ? 'check' : 'visibility' }}
                            </mat-icon>
                          </div>
                          <p class="text-[10px] sm:text-[11px] font-bold text-slate-900 dark:text-white truncate w-full">No Covering</p>
                          <span class="text-[8px] font-extrabold uppercase mt-0.5"
                                [class.text-emerald-600]="selfieChecklist().noCapNoMask"
                                [class.dark:text-emerald-400]="selfieChecklist().noCapNoMask"
                                [class.text-slate-400]="!selfieChecklist().noCapNoMask">
                            {{ selfieChecklist().noCapNoMask ? 'Pass' : 'Check' }}
                          </span>
                        </div>

                        <!-- Step 4: Dress Presence (Confirming only clothing worn) -->
                        <div 
                          class="flex flex-col items-center justify-center p-2 rounded-xl text-center transition-all duration-300 min-w-0"
                          [class.bg-emerald-500/15]="selfieChecklist().properlyDressed"
                          [class.border]="selfieChecklist().properlyDressed"
                          [class.border-emerald-500/30]="selfieChecklist().properlyDressed"
                          [class.bg-white/50]="!selfieChecklist().properlyDressed"
                          [class.dark:bg-white/5]="!selfieChecklist().properlyDressed"
                        >
                          <div class="w-5 h-5 rounded-full flex items-center justify-center mb-1 shrink-0"
                               [class.bg-emerald-500]="selfieChecklist().properlyDressed"
                               [class.text-white]="selfieChecklist().properlyDressed"
                               [class.bg-black/10]="!selfieChecklist().properlyDressed"
                               [class.dark:bg-white/10]="!selfieChecklist().properlyDressed"
                               [class.text-slate-400]="!selfieChecklist().properlyDressed">
                            <mat-icon class="text-[13px] h-[13px] w-[13px]">
                              {{ selfieChecklist().properlyDressed ? 'check' : 'checkroom' }}
                            </mat-icon>
                          </div>
                          <p class="text-[10px] sm:text-[11px] font-bold text-slate-900 dark:text-white truncate w-full">Dress Presence</p>
                          <span class="text-[8px] font-extrabold uppercase mt-0.5"
                                [class.text-emerald-600]="selfieChecklist().properlyDressed"
                                [class.dark:text-emerald-400]="selfieChecklist().properlyDressed"
                                [class.text-slate-400]="!selfieChecklist().properlyDressed">
                            {{ selfieChecklist().properlyDressed ? 'Pass' : 'Check' }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                } @else if (selfieImage()) {
                  <div class="relative group animate-ios-fade text-center">
                    @if (!isFaceVerified() || !kicgleId()) {
                      <div class="absolute top-2 right-2 z-20">
                        <button type="button" (click)="removeSelfie()" class="w-8 h-8 bg-black/60 backdrop-blur-md text-white rounded-full flex items-center justify-center hover:bg-red-500 transition-colors shadow-lg" title="Retake Photo">
                          <mat-icon class="text-[18px]">refresh</mat-icon>
                        </button>
                      </div>
                    } @else {
                      <!-- Retake button permanently removed once face verification succeeds & unique Kicgle ID generated -->
                      <div class="absolute top-2 right-2 z-20 w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center shadow-lg border-2 border-white/50 animate-ios-fade" title="Face & Kicgle ID Verified">
                        <mat-icon class="text-[18px] font-black">verified</mat-icon>
                      </div>
                    }
                    <div class="relative aspect-square w-40 mx-auto rounded-full overflow-hidden border-4 border-emerald-500/50 shadow-2xl">
                      <img [src]="selfieImage()" alt="Selfie Preview" class="w-full h-full object-cover">
                    </div>
                    <!-- Formatted 17-digit Kicgle ID display -->
                    <div class="mt-3 px-3.5 py-1.5 rounded-xl bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 inline-block">
                      <p class="text-[10px] font-black uppercase tracking-widest opacity-50">Kicgle ID</p>
                      <p class="text-xs font-mono font-black text-ios-blue dark:text-ios-blue-dark tracking-wider">{{ formattedKicgleId() }}</p>
                    </div>
                  </div>
                }
              </div>
            }

            @if (panVerified() && (accountType() === 'organization' || accountType() === 'country' || (accountType() === 'person' && calculateAge(panDetails()?.dob || '') < 18))) {
              <div class="mt-8 space-y-6 animate-ios-slide">
                <div class="flex items-center justify-between px-1">
                  <div class="flex flex-col">
                    <p class="text-[13px] font-black uppercase tracking-widest text-ios-blue/80 dark:text-ios-blue-dark/80">Authorized Persons</p>
                    <p class="text-[11px] opacity-40 font-bold">Legal representatives & guardians</p>
                  </div>
                  <span class="text-[10px] font-black bg-ios-blue/10 text-ios-blue px-3 py-1 rounded-full uppercase tracking-tighter border border-ios-blue/20">Required</span>
                </div>
                
                <div class="bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 shadow-2xl overflow-hidden divide-y divide-white/5">
                  @for (person of selectedAuthorizedPersons(); track person.uid; let i = $index) {
                    <div class="flex items-center gap-4 p-4 group hover:bg-white/5 transition-all duration-300">
                      <div class="relative">
                        @if (person.photo) {
                          <img [src]="person.photo" alt="Person Photo" class="w-12 h-12 rounded-2xl object-cover shadow-2xl border border-white/20">
                        } @else {
                          <div class="w-12 h-12 rounded-2xl bg-ios-blue/10 flex items-center justify-center border border-ios-blue/20">
                            <mat-icon class="text-ios-blue text-[24px]">person</mat-icon>
                          </div>
                        }
                        <div class="absolute -top-1 -right-1 w-5 h-5 bg-green-500 text-white rounded-full flex items-center justify-center shadow-lg border-2 border-slate-900">
                          <mat-icon class="text-[10px] h-[10px] w-[10px] font-bold">check</mat-icon>
                        </div>
                      </div>
                      <div class="flex-1">
                        <p class="text-[15px] font-black text-ios-title tracking-tight">{{ person.name }}</p>
                        <p class="text-[12px] font-bold text-ios-blue/60 uppercase tracking-widest">{{ person.role }}</p>
                      </div>
                      <button type="button" (click)="removeAuthorizedPerson(i)" class="w-10 h-10 flex items-center justify-center text-red-500/40 hover:text-red-500 hover:bg-red-500/10 rounded-xl transition-all duration-300">
                        <mat-icon class="text-[22px]">delete_sweep</mat-icon>
                      </button>
                    </div>
                  }
                  @if (selectedAuthorizedPersons().length === 0) {
                    <div class="p-8 text-center opacity-30">
                      <mat-icon class="text-[48px] h-[48px] w-[48px] mb-2">group_add</mat-icon>
                      <p class="text-[13px] font-bold">No authorized persons added yet</p>
                    </div>
                  }
                </div>

                <div class="p-1 bg-white/5 rounded-[32px] border border-white/10 shadow-inner overflow-hidden">
                  <div class="p-5 space-y-4">
                    <div class="relative">
                      @if (!newAuthPersonUid) {
                        <div class="ios-input-container group hover:border-ios-blue/40 bg-black/20 focus-within:bg-black/40 transition-all">
                          <mat-icon class="text-ios-blue/30 group-focus-within:text-ios-blue mr-3 text-[22px]">person_search</mat-icon>
                          <input 
                            type="text" 
                            [ngModel]="personSearchTerm()" 
                            (ngModelChange)="personSearchTerm.set($event)"
                            name="personSearchTerm" 
                            placeholder="Search by name or @username..." 
                            class="ios-input text-[15px] font-medium"
                          >
                        </div>
                        
                        @if (personSearchTerm() && filteredPersons().length > 0) {
                          <div class="absolute top-full left-0 w-full mt-3 bg-slate-900/95 backdrop-blur-md rounded-3xl shadow-2xl border border-white/10 z-50 overflow-hidden animate-ios-slide">
                            @for (p of filteredPersons(); track p.uid) {
                              <div (click)="selectPersonForAuth(p)" (keydown.enter)="selectPersonForAuth(p)" tabindex="0" class="flex items-center gap-4 p-4 hover:bg-ios-blue/10 cursor-pointer transition-all duration-300 border-b border-white/5 last:border-0 group">
                                @if (p.photo) {
                                  <img [src]="p.photo" alt="Person Photo" class="w-11 h-11 rounded-xl object-cover shadow-lg group-hover:scale-110 transition-transform">
                                } @else {
                                  <div class="w-11 h-11 rounded-xl bg-ios-blue/10 flex items-center justify-center border border-ios-blue/20">
                                    <mat-icon class="text-ios-blue text-[22px]">person</mat-icon>
                                  </div>
                                }
                                <div class="flex flex-col">
                                  <span class="text-[15px] font-bold text-white group-hover:text-ios-blue transition-colors">{{ p.name }}</span>
                                  <span class="text-[12px] font-bold opacity-40 tracking-wider">&#64;{{ p.username }}</span>
                                </div>
                              </div>
                            }
                          </div>
                        }
                      } @else {
                        <div class="flex items-center justify-between bg-ios-blue/10 border border-ios-blue/30 p-4 rounded-2xl animate-ios-fade">
                          <div class="flex items-center gap-4">
                            @if (newAuthPersonPhoto) {
                              <img [src]="newAuthPersonPhoto" alt="Person Photo" class="w-12 h-12 rounded-xl object-cover shadow-2xl">
                            } @else {
                              <div class="w-12 h-12 rounded-xl bg-ios-blue/20 flex items-center justify-center border border-ios-blue/30">
                                <mat-icon class="text-ios-blue text-[24px]">person</mat-icon>
                              </div>
                            }
                            <div class="flex flex-col">
                              <span class="text-[15px] font-black text-ios-blue tracking-tight">{{ newAuthPersonName }}</span>
                              <span class="text-[12px] font-bold text-ios-blue/60 tracking-wider">&#64;{{ newAuthPersonUsername }}</span>
                            </div>
                          </div>
                          <button type="button" (click)="clearSelectedPersonForAuth()" class="w-10 h-10 flex items-center justify-center text-ios-blue hover:bg-ios-blue/20 rounded-xl transition-all">
                            <mat-icon class="text-[20px]">close</mat-icon>
                          </button>
                        </div>
                      }
                    </div>

                    <div class="flex flex-col sm:flex-row gap-3 items-stretch">
                      <div class="flex-1 ios-input-container group hover:border-ios-blue/40 bg-black/20 focus-within:bg-black/40 transition-all">
                        <mat-icon class="text-ios-blue/30 group-focus-within:text-ios-blue mr-3 text-[22px]">verified_user</mat-icon>
                        <select [(ngModel)]="newAuthPersonRole" name="newAuthPersonRole" class="ios-input text-[15px] font-bold bg-transparent outline-none cursor-pointer appearance-none">
                          <option value="" disabled selected>Select Role</option>
                          @for (role of ((accountType() === 'organization' || accountType() === 'country') ? orgRoles : minorRoles); track role) {
                            <option [value]="role">{{ role }}</option>
                          }
                        </select>
                        <mat-icon class="text-ios-blue/30 ml-auto pointer-events-none">expand_more</mat-icon>
                      </div>
                      <button 
                        type="button" 
                        (click)="addAuthorizedPerson()" 
                        [disabled]="!newAuthPersonUid || !newAuthPersonRole" 
                        class="h-[52px] px-8 bg-ios-blue text-white text-[15px] font-black rounded-2xl disabled:opacity-30 disabled:grayscale transition-all shadow-xl shadow-ios-blue/30 active:scale-95 flex items-center justify-center gap-2"
                      >
                        <mat-icon>add_circle</mat-icon>
                        Add
                      </button>
                    </div>
                  </div>
                </div>
                
                @if (!isAuthorizedPersonsValid()) {
                  <div class="flex items-center gap-3 px-4 py-3 bg-red-500/5 border border-red-500/10 rounded-2xl animate-pulse">
                    <mat-icon class="text-red-500 text-[20px]">warning</mat-icon>
                    <p class="text-[12px] font-black text-red-500/80 uppercase tracking-tight">
                      {{ (accountType() === 'organization' || accountType() === 'country') ? 'Required: One Managing Director, Partner, or Trustee.' : 'Required: One Parent or Guardian.' }}
                    </p>
                  </div>
                }
              </div>
            }
          }
        </div>
      }

      <!-- Signup Step 3 Fields: Username -> Email -> Phone -> Password (Visible together, disabled sequentially) -->
      @if (mode() === 'signup' && panDetails() && (accountType() === 'organization' || accountType() === 'country' || selfieImage()) && !existingPanAccount()) {
        <div class="space-y-4 animate-ios-slide">
          
          <!-- Field 1: Username -->
          <div class="space-y-2">
            <div class="flex items-center justify-between px-1">
              <div class="flex flex-col">
                <p class="text-[13px] font-black uppercase tracking-widest text-ios-blue dark:text-ios-blue-dark">Choose Username</p>
                <p class="text-[10px] opacity-40 font-bold">1-4 digits required</p>
              </div>
              @if (usernameStatus() === 'available') {
                <span class="text-[10px] font-black text-green-500 bg-green-500/10 px-2 py-0.5 rounded-full uppercase tracking-tighter">Available</span>
              }
            </div>
            <div class="relative group">
              <div class="relative ios-input-container group hover:border-ios-blue/40 transition-all duration-500 items-center bg-black/20 focus-within:bg-black/40 p-1 pl-4">
                <mat-icon class="text-gray-400 group-hover:text-ios-blue transition-colors text-xl w-5 h-5 flex items-center justify-center mr-2 shrink-0">alternate_email</mat-icon>
                <div class="flex items-center flex-1">
                  <span class="text-ios-blue font-black text-[17px] tracking-tight">{{ usernamePrefix() }}</span>
                  <input 
                    type="text" 
                    [(ngModel)]="usernameSuffix" 
                    (ngModelChange)="onUsernameChange()"
                    name="usernameSuffix"
                    id="username-input"
                    placeholder="____"
                    maxlength="4"
                    class="ios-input pl-0 font-black text-[17px] tracking-widest bg-transparent border-none focus:ring-0 w-full"
                    (keypress)="onUsernameKeypress($event)"
                  >
                </div>
                <div class="pr-4 flex items-center">
                  @if (usernameStatus() === 'checking') {
                    <div class="w-5 h-5 border-3 border-ios-blue/20 border-t-ios-blue rounded-full animate-spin"></div>
                  } @else if (usernameStatus() === 'available') {
                    <div class="w-7 h-7 bg-green-500/20 rounded-full flex items-center justify-center">
                      <mat-icon class="text-green-500 text-sm">check</mat-icon>
                    </div>
                  } @else if (usernameStatus() === 'taken' || usernameStatus() === 'invalid') {
                    <div class="w-7 h-7 bg-red-500/20 rounded-full flex items-center justify-center">
                      <mat-icon class="text-red-500 text-sm">close</mat-icon>
                    </div>
                  }
                </div>
              </div>
            </div>
            @if (usernameStatus() === 'taken') {
              <p class="text-[11px] font-bold text-red-500 px-1">Username is already taken.</p>
            } @else if (usernameStatus() === 'invalid') {
              <p class="text-[11px] font-bold text-red-500 px-1">Username must be 1-4 digits.</p>
            }
          </div>

          <!-- Field 2: Email Address (Disabled until Username is available) -->
          <div class="space-y-2 transition-all duration-300" [class.opacity-50]="usernameStatus() !== 'available'">
            <div class="flex justify-between items-center px-1">
              <label for="email-input-signup" class="text-[13px] font-black uppercase tracking-widest text-ios-blue dark:text-ios-blue-dark">Email Address</label>
              @if (emailStatus() === 'available') {
                <span class="text-[10px] font-black text-green-500 bg-green-500/10 px-2 py-0.5 rounded-full uppercase tracking-tighter">Available</span>
              }
            </div>
            <div class="relative group">
              <div class="relative ios-input-container group hover:border-ios-blue/40 transition-all duration-500 bg-black/20 focus-within:bg-black/40 flex items-center justify-between">
                <mat-icon class="text-gray-400 group-hover:text-ios-blue transition-colors text-xl w-5 h-5 flex items-center justify-center mr-3 shrink-0">alternate_email</mat-icon>
                <input 
                  type="email" 
                  [ngModel]="email" 
                  (ngModelChange)="email = ($event || '').toLowerCase(); onEmailChange()"
                  (input)="email = (email || '').toLowerCase()"
                  name="email"
                  id="email-input-signup"
                  required
                  [disabled]="usernameStatus() !== 'available'"
                  class="ios-input font-bold text-[16px] flex-1 min-w-0 w-full disabled:cursor-not-allowed lowercase"
                  placeholder="name@example.com"
                  autocomplete="email"
                >
                <div class="flex items-center ml-2 shrink-0 gap-1.5">
                  @if (emailStatus() === 'checking') {
                    <div class="w-5 h-5 border-2 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin"></div>
                  } @else if (emailStatus() === 'available') {
                    <mat-icon class="text-green-500 text-base">check_circle</mat-icon>
                  } @else if (emailStatus() === 'taken') {
                    <mat-icon class="text-red-500 text-base">cancel</mat-icon>
                  }
                </div>
              </div>
            </div>
            @if (usernameStatus() !== 'available') {
              <p class="text-[11px] text-slate-500 dark:text-zinc-400 px-1 font-medium">Validate username to unlock email address.</p>
            } @else if (emailStatus() === 'taken') {
              <p class="text-[11px] text-red-500 px-1 font-medium">Email already registered.</p>
            }
          </div>

          <!-- Field 3: Phone Number (Disabled until Email is available; ONLY leading phone icon, no +91, no trailing icon) -->
          <div class="space-y-2 transition-all duration-300" [class.opacity-50]="emailStatus() !== 'available'">
            <div class="flex justify-between items-center px-1">
              <label for="phone-input" class="text-[13px] font-black uppercase tracking-widest text-ios-blue dark:text-ios-blue-dark">Phone Number</label>
              @if (phoneStatus() === 'available' || phoneNumber.length === 10) {
                <span class="text-[10px] font-black text-green-500 bg-green-500/10 px-2 py-0.5 rounded-full uppercase tracking-tighter">Available</span>
              }
            </div>
            <div class="relative ios-input-container group hover:border-ios-blue/40 transition-all duration-300 bg-black/20 focus-within:bg-black/40 flex items-center justify-between">
              <div class="flex items-center flex-1 min-w-0">
                <mat-icon class="text-gray-400 group-hover:text-ios-blue transition-colors text-xl w-5 h-5 flex items-center justify-center mr-2 shrink-0">phone</mat-icon>
                <input 
                  type="tel" 
                  [(ngModel)]="phoneNumber" 
                  (ngModelChange)="onPhoneChange()"
                  name="phoneNumber"
                  id="phone-input"
                  class="ios-input font-bold text-[16px] flex-1 min-w-0 w-full disabled:cursor-not-allowed"
                  placeholder="Phone number (10 digits)"
                  autocomplete="tel"
                  maxlength="10"
                  [disabled]="emailStatus() !== 'available'"
                  required
                >
              </div>
              <div class="flex items-center ml-2 shrink-0 gap-1.5">
                @if (phoneStatus() === 'checking') {
                  <div class="w-5 h-5 border-2 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin"></div>
                } @else if (phoneStatus() === 'available') {
                  <mat-icon class="text-green-500 text-base">check_circle</mat-icon>
                } @else if (phoneStatus() === 'taken') {
                  <mat-icon class="text-red-500 text-base">cancel</mat-icon>
                }
              </div>
            </div>
            @if (emailStatus() !== 'available') {
              <p class="text-[11px] text-slate-500 dark:text-zinc-400 px-1 font-medium">Validate email to unlock phone number.</p>
            } @else if (phoneStatus() === 'taken') {
              <p class="text-[11px] text-red-500 px-1 font-medium">Phone number already registered.</p>
            } @else if (phoneNumber.length > 0 && phoneNumber.length < 10) {
              <p class="text-[11px] text-amber-500 px-1 font-medium">Enter 10 digits ({{ 10 - phoneNumber.length }} digits left).</p>
            }
          </div>

          <!-- Field 4: Password (Disabled until Phone Number is available; for Person accounts) -->
          @if (accountType() === 'person') {
            <div class="space-y-2 transition-all duration-300" [class.opacity-50]="phoneNumber.length < 10 || phoneStatus() !== 'available'">
              <div class="flex justify-between items-center px-1">
                <label for="password-input-signup" class="text-[13px] font-black uppercase tracking-widest text-ios-blue dark:text-ios-blue-dark">Password</label>
                @if (password().length >= 8) {
                  <span class="text-[10px] font-black text-green-500 bg-green-500/10 px-2 py-0.5 rounded-full uppercase tracking-tighter">Valid</span>
                }
              </div>
              <div class="relative group">
                <div class="relative ios-input-container group hover:border-ios-blue/40 transition-all duration-500 bg-black/20 focus-within:bg-black/40 flex items-center justify-between">
                  <mat-icon class="text-gray-400 group-hover:text-ios-blue transition-colors text-xl w-5 h-5 flex items-center justify-center mr-3 shrink-0">lock</mat-icon>
                  <input 
                    [type]="showPassword() ? 'text' : 'password'" 
                    [ngModel]="password()" 
                    (ngModelChange)="password.set($event)"
                    name="password"
                    id="password-input-signup"
                    required
                    [disabled]="phoneNumber.length < 10 || phoneStatus() !== 'available'"
                    class="ios-input font-bold text-[16px] flex-1 min-w-0 w-full disabled:cursor-not-allowed"
                    placeholder="Minimum 8 characters"
                    autocomplete="new-password"
                  >
                  <button 
                    type="button" 
                    (click)="togglePassword()" 
                    [disabled]="phoneNumber.length < 10"
                    class="text-ios-blue/40 hover:text-ios-blue transition-colors ml-2 shrink-0 flex items-center justify-center p-1 rounded-full hover:bg-white/5 disabled:opacity-30 disabled:pointer-events-none" 
                    [attr.aria-label]="showPassword() ? 'Hide password' : 'Show password'"
                  >
                    <mat-icon class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl w-5 h-5 flex items-center justify-center">
                      {{ showPassword() ? 'visibility_off' : 'visibility' }}
                    </mat-icon>
                  </button>
                </div>
              </div>
              @if (phoneNumber.length < 10 || phoneStatus() !== 'available') {
                <p class="text-[11px] text-slate-500 dark:text-zinc-400 px-1 font-medium">Validate phone number to unlock password.</p>
              } @else if (password().length > 0 && password().length < 8) {
                <p class="text-[11px] text-amber-500 dark:text-amber-400 px-1 font-medium">Password must be at least 8 characters ({{ 8 - password().length }} required).</p>
              }
            </div>
          }

        </div>
      }

      <!-- Email & Password Fields for Signin / Forgot / ResetPassword Modes -->
      @if (mode() !== 'signup') {
        @if (mode() !== 'resetPassword') {
          <div class="space-y-3 animate-ios-slide">
            <div class="flex justify-between items-center px-1">
              <label for="email-input-signin" class="text-[13px] font-black uppercase tracking-widest text-ios-blue/80 dark:text-ios-blue-dark/80">
                Email, Phone, or Username
              </label>
            </div>
            <div class="relative group">
              <div class="relative ios-input-container group hover:border-ios-blue/40 transition-all duration-500 bg-black/20 focus-within:bg-black/40 flex items-center justify-between">
                <mat-icon class="text-gray-400 group-hover:text-ios-blue transition-colors text-xl w-5 h-5 flex items-center justify-center mr-3 shrink-0">account_circle</mat-icon>
                <input 
                  type="text" 
                  [(ngModel)]="email" 
                  (ngModelChange)="onEmailChange()"
                  name="email"
                  id="email-input-signin"
                  required
                  class="ios-input font-bold text-[16px] flex-1 min-w-0 w-full"
                  placeholder="Username, Phone, or Email"
                  autocomplete="username"
                >
                <div class="flex items-center ml-2 shrink-0 gap-1.5">
                  @if (userVerificationStatus() === 'checking') {
                    <div class="w-5 h-5 border-2 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin"></div>
                  } @else if (userVerificationStatus() === 'verified') {
                    <mat-icon class="text-green-500 text-base">check_circle</mat-icon>
                  } @else if (userVerificationStatus() === 'failed' && email.trim()) {
                    <mat-icon class="text-red-500 text-base">cancel</mat-icon>
                  }
                  @if (email) {
                    <button
                      type="button"
                      (click)="clearIdentifier()"
                      class="text-ios-blue/30 hover:text-ios-blue transition-colors p-1 rounded-full hover:bg-white/5 flex items-center justify-center"
                      aria-label="Clear field"
                    >
                      <mat-icon class="text-sm">close</mat-icon>
                    </button>
                  }
                </div>
              </div>
            </div>
            
            @if (matchedAccount()?.accountType === 'person' && userVerificationStatus() === 'verified') {
              <div class="flex items-center gap-3.5 bg-white/40 dark:bg-black/40 border border-white/40 dark:border-white/10 p-3.5 rounded-[22px] mt-2 backdrop-blur-xl shadow-sm animate-ios-fade">
                @if (matchedAccount()?.photo) {
                  <img [src]="matchedAccount()?.photo" alt="Profile" class="w-11 h-11 rounded-full object-cover shadow-sm shrink-0">
                } @else {
                  <div class="w-11 h-11 rounded-full bg-ios-blue/10 text-ios-blue flex items-center justify-center font-bold shrink-0">
                    <mat-icon class="text-xl">person</mat-icon>
                  </div>
                }
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-1.5 min-w-0">
                    <h4 class="text-sm font-bold text-gray-900 dark:text-white truncate leading-tight">
                      {{ matchedAccount()?.name }}
                    </h4>
                    <mat-icon class="text-ios-blue text-[15px] shrink-0">verified</mat-icon>
                  </div>
                  <p class="text-xs font-medium text-gray-500 dark:text-zinc-400 truncate mt-0.5">
                    @{{ matchedAccount()?.username ? matchedAccount()?.username?.toLowerCase() : 'user' }}
                  </p>
                </div>
              </div>
            }
          </div>
        }

        <!-- Password Field for Signin or ResetPassword -->
        @if (mode() === 'signin' || mode() === 'resetPassword') {
          <div class="space-y-3 animate-ios-slide mt-4">
            <div class="flex justify-between items-center px-1">
              <label for="password-input-signin" class="text-[13px] font-black uppercase tracking-widest text-ios-blue/80 dark:text-ios-blue-dark/80">
                {{ mode() === 'resetPassword' ? 'New Password' : 'Password' }}
              </label>
              @if (mode() === 'signin') {
                <button type="button" (click)="mode.set('forgot')" class="text-[13px] font-black text-ios-blue dark:text-ios-blue-dark hover:opacity-70 transition-opacity uppercase tracking-tighter">Forgot?</button>
              }
            </div>
            <div class="relative group">
              <div 
                class="relative ios-input-container group hover:border-ios-blue/40 transition-all duration-500 bg-black/20 focus-within:bg-black/40 flex items-center justify-between"
                [class.opacity-40]="mode() === 'signin' && userVerificationStatus() !== 'verified'"
                [class.cursor-not-allowed]="mode() === 'signin' && userVerificationStatus() !== 'verified'"
              >
                <mat-icon class="text-gray-400 group-hover:text-ios-blue transition-colors text-xl w-5 h-5 flex items-center justify-center mr-3 shrink-0">lock</mat-icon>
                <input 
                  [type]="showPassword() ? 'text' : 'password'" 
                  [ngModel]="password()" 
                  (ngModelChange)="password.set($event)"
                  name="password"
                  id="password-input-signin"
                  required
                  [disabled]="mode() === 'signin' && userVerificationStatus() !== 'verified'"
                  class="ios-input font-bold text-[16px] flex-1 min-w-0 w-full disabled:cursor-not-allowed"
                  placeholder="••••••••"
                  [autocomplete]="mode() === 'resetPassword' ? 'new-password' : 'current-password'"
                >
                <button 
                  type="button" 
                  (click)="togglePassword()" 
                  [disabled]="mode() === 'signin' && userVerificationStatus() !== 'verified'"
                  class="text-ios-blue/40 hover:text-ios-blue transition-colors ml-2 shrink-0 flex items-center justify-center p-1 rounded-full hover:bg-white/5 disabled:opacity-30 disabled:pointer-events-none" 
                  [attr.aria-label]="showPassword() ? 'Hide password' : 'Show password'"
                >
                  <mat-icon class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl w-5 h-5 flex items-center justify-center">
                    {{ showPassword() ? 'visibility_off' : 'visibility' }}
                  </mat-icon>
                </button>
              </div>
            </div>
            @if (mode() === 'signin' && userVerificationStatus() !== 'verified') {
              <p class="text-[11px] text-slate-500 dark:text-zinc-400 px-1 font-medium">Verify your username, phone number, or email above to unlock the password field.</p>
            }
          </div>
        }
      }


      <!-- Actions -->
      <div class="flex flex-col gap-4 pt-4">
        <button 
          type="submit" 
          [disabled]="!isFormValid() || loading()"
          class="ios-button-primary w-full h-[60px] text-[18px] font-bold shadow-2xl transition-all duration-500 flex items-center justify-center gap-3 relative overflow-hidden group text-white"
          [class.!bg-ios-blue]="isFormValid() || loading()"
          [class.!opacity-100]="isFormValid() || loading()"
          [class.bg-zinc-400]="!isFormValid() && !loading()"
          [class.dark:bg-zinc-700]="!isFormValid() && !loading()"
          [class.opacity-40]="!isFormValid() && !loading()"
          [class.cursor-not-allowed]="!isFormValid() && !loading()"
          [class.hover:shadow-ios-blue/50]="isFormValid() && !loading()"
          [class.hover:-translate-y-1.5]="isFormValid() && !loading()"
          [class.shimmer]="loading()"
        >
          <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
          @if (loading()) {
            <div class="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
            <span class="font-bold tracking-wide text-white">Processing...</span>
          } @else {
            <span class="font-bold tracking-wide">
              @switch (mode()) {
                @case ('forgot') { Send Reset Link }
                @case ('signin') { Sign In }
                @case ('signup') { {{ currentStep() === 3 ? 'Complete Sign Up' : 'Continue' }} }
                @case ('resetPassword') { Update Password }
              }
            </span>
            <mat-icon class="text-[22px] group-hover:translate-x-1 transition-transform">
              {{ mode() === 'signup' ? 'arrow_forward' : 'login' }}
            </mat-icon>
          }
        </button>

        <div class="flex flex-col items-center gap-6 mt-4">
          <button type="button" (click)="toggleMode()" class="flex items-center gap-2 px-6 py-3 rounded-full bg-ios-blue/5 hover:bg-ios-blue/10 text-ios-blue dark:text-ios-blue-dark transition-all duration-300 group">
            @switch (mode()) {
              @case ('signin') { 
                <span class="text-[14px] font-bold">Create a new account</span>
                <mat-icon class="text-[18px] group-hover:translate-x-1 transition-transform">chevron_right</mat-icon>
              }
              @case ('signup') { 
                <span class="text-[14px] font-bold">Sign in instead</span>
                <mat-icon class="text-[18px] group-hover:translate-x-1 transition-transform">chevron_right</mat-icon>
              }
              @case ('forgot') { 
                <span class="text-[14px] font-bold">Back to sign in</span>
                <mat-icon class="text-[18px] group-hover:-translate-x-1 transition-transform">chevron_left</mat-icon>
              }
              @case ('resetPassword') { 
                <span class="text-[14px] font-bold">Back to sign in</span>
                <mat-icon class="text-[18px] group-hover:-translate-x-1 transition-transform">chevron_left</mat-icon>
              }
            }
          </button>

        </div>
      </div>
    </form>
    }
    @if (!authService.isSigningUp() && (mode() === 'verifyEmail' || mode() === 'recoverEmail')) {
      <div class="flex flex-col items-center py-10">
        <div class="w-16 h-16 border-4 border-ios-blue/20 border-t-ios-blue rounded-full animate-spin mb-8"></div>
        <p class="text-ios-body text-center opacity-70">Please wait while we process your request...</p>
      </div>
    } @else if (mode() === 'actionSuccess') {
      <div class="flex flex-col items-center py-10 text-center">
        <div class="w-20 h-20 bg-ios-blue/10 rounded-full flex items-center justify-center mb-8 animate-ios-fade">
          <mat-icon class="text-ios-blue text-4xl">check_circle</mat-icon>
        </div>
        <h2 class="text-2xl font-bold mb-4">Action Completed</h2>
        <p class="text-ios-body opacity-70 mb-10">Your request has been successfully processed. You can now sign in to your account.</p>
        <button (click)="mode.set('signin')" class="ios-button-primary px-10 h-[54px]">Go to Sign In</button>
      </div>
    }

  </div>
</div>
}
  `,
})
export class AuthView implements OnInit, OnDestroy {
  authService = inject(AuthService);
  notificationService = inject(NotificationService);
  aiService = inject(AiService);
  route = inject(ActivatedRoute);
  router = inject(Router);

  kicgleId = signal<string>(this.generateKicgleId());
  isFaceVerified = signal<boolean>(false);

  generateKicgleId(): string {
    let result = '';
    for (let i = 0; i < 17; i++) {
      result += Math.floor(Math.random() * 10).toString();
    }
    return result;
  }

  async isKicgleIdAvailable(id: string): Promise<boolean> {
    if (!id || id.length !== 17) return false;
    try {
      const accountsRef = collection(db, 'accounts');
      const q = query(accountsRef, where('kicgle', '==', id), limit(1));
      const snap = await getDocs(q);
      return snap.empty;
    } catch (err) {
      console.warn('Backend Kicgle ID uniqueness check warning:', err);
      return true;
    }
  }

  async generateUniqueKicgleId(): Promise<string> {
    let isAllocated = true;
    let candidateId = '';
    let loopCount = 0;
    const maxLoops = 30;

    while (isAllocated && loopCount < maxLoops) {
      loopCount++;
      candidateId = this.generateKicgleId();
      const isAvailable = await this.isKicgleIdAvailable(candidateId);
      if (isAvailable) {
        isAllocated = false;
      } else {
        console.warn(`Collision detected on attempt ${loopCount} for Kicgle ID: ${candidateId}. Auto-regenerating immediate new candidate...`);
      }
    }

    this.kicgleId.set(candidateId);
    return candidateId;
  }

  formattedKicgleId = computed(() => {
    const id = this.kicgleId();
    if (!id || id.length !== 17) return '';
    // Formatted in groups as XXXX XXXX X XXXX XXXX
    return `${id.slice(0, 4)} ${id.slice(4, 8)} ${id.slice(8, 9)} ${id.slice(9, 13)} ${id.slice(13, 17)}`;
  });

  mode = signal<AuthMode>('signin');
  email = '';
  phoneNumber = '';
  password = signal('');
  showPassword = signal(false);
  loading = signal(false);
  oobCode: string | null = null;

  panInputMethod = signal<'upload' | 'camera'>('upload');
  isPanCameraActive = signal(false);
  panCameraLoading = signal(false);
  panFacingMode = signal<'environment' | 'user'>('environment');
  panCameraError = signal<string | null>(null);
  panValidationError = signal<string | null>(null);
  private panStream: MediaStream | null = null;

  panImage = signal<string | null>(null);
  panDetails = signal<{name: string, dob: string, panNumber: string} | null>(null);
  usernamePrefix = signal('');
  usernameSuffix = signal('');
  accountType = signal('');
  loadingPan = signal(false);

  existingPanAccount = signal<{photo: string | null, name: string, username: string} | null>(null);
  panVerified = signal(false);
  selfieImage = signal<string | null>(null);
  selfieLoading = signal(false);
  isCameraActive = signal(false);
  isValidatingPhoto = signal(false);
  capturedPhotoTemp = signal<string | null>(null);
  selfieValidationError = signal<string | null>(null);
  private isVerifyingFrame = false;
  selfieChecklist = signal({ 
    clearLighting: false, 
    faceStraightAndLive: false, 
    noCapNoMask: false, 
    properlyDressed: false 
  });

  allChecksPassed = computed(() => {
    const c = this.selfieChecklist();
    return c.clearLighting && c.faceStraightAndLive && c.noCapNoMask && c.properlyDressed;
  });

  passedChecksCount = computed(() => {
    const c = this.selfieChecklist();
    let count = 0;
    if (c.clearLighting) count++;
    if (c.faceStraightAndLive) count++;
    if (c.noCapNoMask) count++;
    if (c.properlyDressed) count++;
    return count;
  });
  private selfieStream: MediaStream | null = null;
  private selfieInterval: ReturnType<typeof setInterval> | null = null;

  usernameCheck$ = new Subject<string>();
  usernameStatus = signal<'checking' | 'available' | 'taken' | 'invalid' | null>(null);

  emailCheck$ = new Subject<string>();
  emailStatus = signal<'checking' | 'available' | 'taken' | 'invalid' | null>(null);

  phoneCheck$ = new Subject<string>();
  phoneStatus = signal<'checking' | 'available' | 'taken' | 'invalid' | null>(null);

  identifierCheck$ = new Subject<string>();
  userVerificationStatus = signal<'checking' | 'verified' | 'failed' | null>(null);
  matchedAccount = signal<{uid: string, name: string, username: string, photo: string | null, email: string, accountType: string} | null>(null);

  availablePersons = signal<{uid: string, name: string, username: string, photo: string | null}[]>([]);
  selectedAuthorizedPersons = signal<{uid: string, name: string, role: string, photo?: string | null}[]>([]);

  currentStep = computed(() => {
    if (!this.panDetails()) return 1;
    if (this.accountType() === 'person' && !this.selfieImage()) return 2;
    if ((this.accountType() === 'organization' || this.accountType() === 'country' || (this.accountType() === 'person' && this.calculateAge(this.panDetails()?.dob || '') < 18)) && this.selectedAuthorizedPersons().length === 0) return 2;
    return 3;
  });
  
  orgRoles = ['Managing Director', 'Director', 'Managing Partner', 'Partner', 'Managing Trustee', 'Trustee'];
  minorRoles = ['Parent', 'Guardian'];

  newAuthPersonUid = '';
  newAuthPersonName = '';
  newAuthPersonUsername = '';
  newAuthPersonPhoto: string | null = null;
  newAuthPersonRole = '';
  
  personSearchTerm = signal('');
  
  filteredPersons = computed(() => {
    const term = this.personSearchTerm().toLowerCase();
    if (!term) return [];
    return this.availablePersons().filter(p => 
      (p.name.toLowerCase().includes(term) || 
      p.username.toLowerCase().includes(term)) &&
      !this.selectedAuthorizedPersons().some(sp => sp.uid === p.uid)
    ).slice(0, 5); // Limit to 5 results
  });

  isFormValid = computed(() => {
    if (this.loading()) return false;
    
    if (this.mode() === 'signin') {
      const verified = this.userVerificationStatus() === 'verified';
      const passValid = !!this.password() && this.password().length >= 8;
      const accountAllowed = this.matchedAccount()?.accountType === 'person';
      return verified && passValid && accountAllowed;
    }
    
    if (this.mode() === 'signup') {
      const step = this.currentStep();
      if (step === 1) return this.panVerified() && !this.existingPanAccount();
      if (step === 2) {
        if (this.accountType() === 'person') {
          const age = this.calculateAge(this.panDetails()?.dob || '');
          if (age < 18) return !!this.selfieImage() && this.isAuthorizedPersonsValid();
          return !!this.selfieImage();
        }
        return this.isAuthorizedPersonsValid();
      }
      if (step === 3) {
        const usernameOk = this.usernameStatus() === 'available';
        const emailOk = this.emailStatus() === 'available';

        if (this.accountType() === 'organization' || this.accountType() === 'country') {
          const phoneOk = this.phoneStatus() === 'available' || (this.phoneNumber.length === 10 && this.phoneStatus() !== 'taken');
          return usernameOk && emailOk && phoneOk;
        }

        const phoneOk = this.phoneNumber.length === 10 && this.phoneStatus() === 'available';
        const passOk = !!this.password() && this.password().length >= 8;
        return usernameOk && emailOk && phoneOk && passOk;
      }
    }
    
    if (this.mode() === 'forgot') return !!this.email && !!this.matchedAccount();
    if (this.mode() === 'resetPassword') return !!this.password() && this.password().length >= 8;
    
    return true;
  });

  selectPersonForAuth(person: {uid: string, name: string, username: string, photo: string | null}) {
    this.newAuthPersonUid = person.uid;
    this.newAuthPersonName = person.name;
    this.newAuthPersonUsername = person.username;
    this.newAuthPersonPhoto = person.photo;
    this.personSearchTerm.set('');
  }

  clearSelectedPersonForAuth() {
    this.newAuthPersonUid = '';
    this.newAuthPersonName = '';
    this.newAuthPersonUsername = '';
    this.newAuthPersonPhoto = null;
  }

  addAuthorizedPerson() {
    if (!this.newAuthPersonUid || !this.newAuthPersonRole) return;
    const person = this.availablePersons().find(p => p.uid === this.newAuthPersonUid);
    if (person) {
      this.selectedAuthorizedPersons.update(list => [...list, {
        uid: person.uid,
        name: person.name,
        role: this.newAuthPersonRole,
        photo: person.photo
      }]);
      this.clearSelectedPersonForAuth();
      this.newAuthPersonRole = '';
    }
  }

  removeAuthorizedPerson(index: number) {
    this.selectedAuthorizedPersons.update(list => {
      const newList = [...list];
      newList.splice(index, 1);
      return newList;
    });
  }

  isAuthorizedPersonsValid(): boolean {
    if (!this.panVerified() || !this.panDetails()) return true;
    
    const accType = this.accountType();
    const age = this.calculateAge(this.panDetails()!.dob);
    
    if (accType === 'organization' || accType === 'country') {
      const hasManaging = this.selectedAuthorizedPersons().some(p => 
        ['Managing Director', 'Managing Partner', 'Managing Trustee'].includes(p.role)
      );
      return hasManaging;
    } else if (accType === 'person' && age < 18) {
      const hasParentOrGuardian = this.selectedAuthorizedPersons().some(p => 
        ['Parent', 'Guardian'].includes(p.role)
      );
      return hasParentOrGuardian;
    }
    
    return true;
  }

  calculateAge(dobString: string): number {
    // Assuming dobString is in DD/MM/YYYY or similar format. Let's try to parse it.
    // PAN card DOB is usually DD/MM/YYYY
    const parts = dobString.split('/');
    let dob: Date;
    if (parts.length === 3) {
      dob = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
    } else {
      dob = new Date(dobString);
    }
    const diff_ms = Date.now() - dob.getTime();
    const age_dt = new Date(diff_ms); 
    return Math.abs(age_dt.getUTCFullYear() - 1970);
  }

  parseDob(dobString: string | undefined): Date | null {
    if (!dobString) return null;
    const parts = dobString.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
        return new Date(year, month, day);
      }
    }
    const d = new Date(dobString);
    return isNaN(d.getTime()) ? null : d;
  }

  async fetchAvailablePersons() {
    try {
      const accountsRef = collection(db, 'accounts');
      const q = query(accountsRef, where('accountType', '==', 'person'));
      const snapshot = await getDocs(q);
      const persons = snapshot.docs.map(doc => ({
        uid: doc.id,
        name: doc.data()['displayName'] || doc.data()['name'] || '',
        username: doc.data()['username'] || '',
        photo: doc.data()['photoURL'] || doc.data()['photo'] || null
      }));
      this.availablePersons.set(persons);
    } catch (err) {
      console.error('Error fetching available persons:', err);
    }
  }

  setPanInputMethod(method: 'upload' | 'camera') {
    this.panInputMethod.set(method);
    this.panValidationError.set(null);
    if (method === 'camera') {
      this.startPanCamera();
    } else {
      this.stopPanCamera();
    }
  }

  async startPanCamera(facing: 'environment' | 'user' = this.panFacingMode()) {
    if (typeof window === 'undefined') return;
    this.panCameraLoading.set(true);
    this.panCameraError.set(null);
    this.stopPanCamera();

    try {
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facing },
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          },
          audio: false
        });
      } catch (err) {
        // Fallback for devices without explicit facingMode support (e.g. desktop webcams)
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false
        });
      }

      this.panStream = stream;
      this.panFacingMode.set(facing);
      this.isPanCameraActive.set(true);

      setTimeout(() => {
        const video = document.getElementById('pan-card-video') as HTMLVideoElement;
        if (video && this.panStream) {
          video.srcObject = this.panStream;
          video.play().catch(e => console.warn('PAN video playback warning:', e));
        }
      }, 150);

      this.notificationService.info('Camera active. Align PAN card inside the frame.');
    } catch (err: any) {
      console.error('PAN Camera access error:', err);
      this.panCameraError.set(err?.message || 'Unable to access camera. Please allow camera permissions or upload the PAN card file.');
      this.notificationService.error('Camera access failed. You can upload the card file instead.');
    } finally {
      this.panCameraLoading.set(false);
    }
  }

  togglePanFacingMode() {
    const nextMode = this.panFacingMode() === 'environment' ? 'user' : 'environment';
    this.startPanCamera(nextMode);
  }

  capturePanPhoto() {
    const video = document.getElementById('pan-card-video') as HTMLVideoElement;
    if (!video || !this.panStream || video.videoWidth === 0) {
      this.notificationService.error('Camera is not ready yet. Please wait a moment.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const capturedDataUrl = canvas.toDataURL('image/jpeg', 0.92);

    this.stopPanCamera();
    this.panImage.set(capturedDataUrl);
    this.notificationService.success('PAN card captured! Extracting identity...');
    this.extractPanDetails(capturedDataUrl, 'image/jpeg');
  }

  stopPanCamera() {
    if (this.panStream) {
      this.panStream.getTracks().forEach(track => track.stop());
      this.panStream = null;
    }
    this.isPanCameraActive.set(false);
    this.panCameraLoading.set(false);
  }

  removePanImage() {
    this.stopPanCamera();
    this.stopCamera();
    this.panValidationError.set(null);
    this.panImage.set(null);
    this.panDetails.set(null);
    this.usernamePrefix.set('');
    this.usernameSuffix.set('');
    this.usernameStatus.set(null);
    this.accountType.set('');
    this.panVerified.set(false);
    this.existingPanAccount.set(null);
    this.selectedAuthorizedPersons.set([]);

    // Cascading reset of all subsequent form fields and states including face verification & Kicgle ID
    this.selfieImage.set(null);
    this.isFaceVerified.set(false);
    this.capturedPhotoTemp.set(null);
    this.selfieValidationError.set(null);
    this.kicgleId.set('');
    this.email = '';
    this.emailStatus.set(null);
    this.phoneNumber = '';
    this.password.set('');
  }

  ngOnDestroy() {
    this.stopPanCamera();
    this.stopCamera();
  }

  async startCamera() {
    if (this.isFaceVerified() && this.kicgleId()) {
      // Do not permit re-capturing once this verified state is reached
      return;
    }
    if (typeof window === 'undefined') return;
    
    this.selfieLoading.set(true);
    try {
      this.selfieStream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      this.isCameraActive.set(true);
      
      // Set video srcObject after a short delay to ensure element is rendered
      setTimeout(() => {
        const video = (document.getElementById('selfie-video') as HTMLVideoElement) || document.querySelector('video');
        if (video && this.selfieStream) {
          video.srcObject = this.selfieStream;
        }
      }, 100);
      
      // Start continuous Gemini verification
      this.selfieInterval = setInterval(() => {
        this.verifySelfieFrame();
      }, 3000);

      this.notificationService.info('Camera active. Please look straight and hold still.');
    } catch (err) {
      console.error('Camera access error:', err);
      this.notificationService.error('Failed to access camera.');
    } finally {
      this.selfieLoading.set(false);
    }
  }

  analyzeFrameLocally(ctx: CanvasRenderingContext2D, width: number, height: number): {
    clearLighting: boolean;
    faceStraightAndLive: boolean;
    noCapNoMask: boolean;
    properlyDressed: boolean;
  } {
    try {
      const step = 8;
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;
      
      let totalLuma = 0;
      let centerLuma = 0;
      let count = 0;
      let centerCount = 0;
      let topLuma = 0;
      let topCount = 0;
      let bottomLuma = 0;
      let bottomCount = 0;

      const centerXMin = width * 0.25;
      const centerXMax = width * 0.75;
      const centerYMin = height * 0.2;
      const centerYMax = height * 0.8;
      const topYMax = height * 0.25;
      const bottomYMin = height * 0.72;

      for (let y = 0; y < height; y += step) {
        for (let x = 0; x < width; x += step) {
          const idx = (y * width + x) * 4;
          const r = data[idx];
          const g = data[idx + 1];
          const b = data[idx + 2];
          const luma = 0.299 * r + 0.587 * g + 0.114 * b;

          totalLuma += luma;
          count++;

          if (x >= centerXMin && x <= centerXMax && y >= centerYMin && y <= centerYMax) {
            centerLuma += luma;
            centerCount++;
          }
          if (y <= topYMax) {
            topLuma += luma;
            topCount++;
          }
          if (y >= bottomYMin) {
            bottomLuma += luma;
            bottomCount++;
          }
        }
      }

      const avgTotalLuma = count > 0 ? totalLuma / count : 0;
      const avgCenterLuma = centerCount > 0 ? centerLuma / centerCount : 0;
      const avgTopLuma = topCount > 0 ? topLuma / topCount : 0;
      const avgBottomLuma = bottomCount > 0 ? bottomLuma / bottomCount : 0;

      // 1. Lightning: Ambient lighting is clear and sufficient
      const clearLighting = avgCenterLuma >= 35 && avgCenterLuma <= 235 && avgTotalLuma >= 30;

      // 2. Face Alignment: Centered human face illuminated and focused
      const faceStraightAndLive = clearLighting && avgCenterLuma >= 40;

      // 3. No Face Covering: Upper head region not obscured by dark opaque mask
      const noCapNoMask = avgTopLuma >= 25;

      // 4. Dress presence: Confirming only that clothing is being worn (lower region presence)
      const properlyDressed = avgBottomLuma >= 20;

      return {
        clearLighting,
        faceStraightAndLive,
        noCapNoMask,
        properlyDressed
      };
    } catch {
      return {
        clearLighting: true,
        faceStraightAndLive: true,
        noCapNoMask: true,
        properlyDressed: true
      };
    }
  }

  async verifySelfieFrame() {
    if (!this.isCameraActive() || this.isValidatingPhoto() || this.capturedPhotoTemp()) return;
    if (this.isVerifyingFrame) return;
    
    const video = (document.getElementById('selfie-video') as HTMLVideoElement) || document.querySelector('video');
    if (!video || video.videoWidth === 0) return;

    this.isVerifyingFrame = true;

    try {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(video, 0, 0);
      
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      const base64Data = imageData.split(',')[1];

      const result = await this.aiService.verifySelfie(base64Data);
      if (result && (result.clearLighting || result.faceStraightAndLive || result.noCapNoMask || result.properlyDressed)) {
        this.selfieChecklist.set({
          clearLighting: !!result.clearLighting,
          faceStraightAndLive: !!result.faceStraightAndLive,
          noCapNoMask: !!result.noCapNoMask,
          properlyDressed: !!result.properlyDressed
        });
      } else {
        const localCheck = this.analyzeFrameLocally(ctx, canvas.width, canvas.height);
        this.selfieChecklist.set(localCheck);
      }
    } catch (err) {
      console.warn('Selfie verification frame warning:', err);
    } finally {
      this.isVerifyingFrame = false;
    }
  }

  stopCamera() {
    if (this.selfieInterval) {
      clearInterval(this.selfieInterval);
      this.selfieInterval = null;
    }
    if (this.selfieStream) {
      this.selfieStream.getTracks().forEach(track => track.stop());
      this.selfieStream = null;
    }
    this.isCameraActive.set(false);
    this.isVerifyingFrame = false;
    this.selfieChecklist.set({
      clearLighting: false,
      faceStraightAndLive: false,
      noCapNoMask: false,
      properlyDressed: false
    });
  }

  async captureSelfie() {
    if (!this.isCameraActive() || this.isValidatingPhoto()) return;
    
    const video = (document.getElementById('selfie-video') as HTMLVideoElement) || document.querySelector('video');
    if (!video || video.videoWidth === 0) {
      this.notificationService.error('Camera feed is not ready. Please hold still.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const capturedDataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const base64Data = capturedDataUrl.split(',')[1];

    // Initiate loading state and hold captured photo for validation
    this.capturedPhotoTemp.set(capturedDataUrl);
    this.isValidatingPhoto.set(true);
    this.selfieValidationError.set(null);

    try {
      this.notificationService.info('Validating captured photo...');
      const validation = await this.aiService.validateCapturedSelfie(base64Data);

      if (validation && validation.isValid && validation.hasHumanFace) {
        // Validated successfully: Finalize verification!
        this.selfieImage.set(capturedDataUrl);
        this.isFaceVerified.set(true);
        this.capturedPhotoTemp.set(null);
        this.selfieValidationError.set(null);
        this.stopCamera();

        // Collision detection and auto-regeneration loop for unique Kicgle ID prior to saving
        await this.generateUniqueKicgleId();
        this.notificationService.success('Face Verification finalized successfully!');
      } else {
        // Validation failed: wrong object, missing face, or criteria failed
        const errorMsg = validation?.reason || 'Validation failed: A clear human face was not detected or criteria not fulfilled.';
        this.selfieValidationError.set(errorMsg);
        this.notificationService.error(errorMsg);
      }
    } catch (err: any) {
      console.error('Selfie validation error:', err);
      const fallbackMsg = err?.message || 'Validation failed. Please retake the photo.';
      this.selfieValidationError.set(fallbackMsg);
      this.notificationService.error(fallbackMsg);
    } finally {
      this.isValidatingPhoto.set(false);
    }
  }

  retakeSelfie() {
    if (this.isFaceVerified() && this.kicgleId()) {
      // Do not permit re-capturing once this verified state is reached
      return;
    }
    this.capturedPhotoTemp.set(null);
    this.selfieValidationError.set(null);
    this.isValidatingPhoto.set(false);
    this.selfieChecklist.set({
      clearLighting: false,
      faceStraightAndLive: false,
      noCapNoMask: false,
      properlyDressed: false
    });

    if (!this.selfieStream || !this.isCameraActive()) {
      this.startCamera();
    } else {
      setTimeout(() => {
        const video = (document.getElementById('selfie-video') as HTMLVideoElement) || document.querySelector('video');
        if (video && this.selfieStream) {
          video.srcObject = this.selfieStream;
          video.play().catch(e => console.warn('Video playback resume warning:', e));
        }
      }, 100);
    }
  }

  removeSelfie() {
    if (this.isFaceVerified() && this.kicgleId()) {
      // Do not permit re-capturing once this verified state is reached
      return;
    }
    this.selfieImage.set(null);
    this.isFaceVerified.set(false);
    this.capturedPhotoTemp.set(null);
    this.selfieValidationError.set(null);
    this.isValidatingPhoto.set(false);
    this.selfieChecklist.set({
      clearLighting: false,
      faceStraightAndLive: false,
      noCapNoMask: false,
      properlyDressed: false
    });
  }

  onPanUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const result = e.target?.result as string;
        this.panImage.set(result);
        this.extractPanDetails(result, file.type);
      };
      reader.readAsDataURL(file);
    }
  }

  async extractPanDetails(imageData: string, mimeType: string) {
    this.loadingPan.set(true);
    this.panValidationError.set(null);
    try {
      const base64Data = imageData.split(',')[1] || imageData;
      const details = await this.aiService.analyzePAN(base64Data, mimeType);
      
      if (!details || !details.isPan || !details.panNumber || details.panNumber.length < 5) {
        const promptMsg = details?.reason || 'Invalid document or object detected. Please align a valid PAN card inside the scanner frame and retake the photo.';
        this.panValidationError.set(promptMsg);
        this.notificationService.error(promptMsg);
        this.removePanImage();
        this.loadingPan.set(false);
        // If camera mode was selected, restart camera so user can immediately retake
        if (this.panInputMethod() === 'camera') {
          this.startPanCamera();
        }
        return;
      }

      // Format name
      if (details.name) {
        details.name = details.name.split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
      }

      this.panDetails.set(details);
      
      this.usernamePrefix.set(details.name.toLowerCase().replace(/\s/g, ''));
      
      // Account type: 4th digit of PAN
      const fourthDigit = details.panNumber[3];
      const accType = fourthDigit === 'P' ? 'person' : fourthDigit === 'G' ? 'country' : 'organization';
      this.accountType.set(accType);

      if (accType === 'organization' || accType === 'country') {
        this.password.set(this.generateStrongPassword());
      }

      // Check if PAN is already registered
      try {
        const accountsRef = collection(db, 'accounts');
        const q = query(accountsRef, where('documentNumber', '==', details.panNumber));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const existingAccount = snapshot.docs[0].data();
          this.existingPanAccount.set({
            photo: existingAccount['photoURL'] || existingAccount['photo'] || null,
            name: existingAccount['name'] || existingAccount['displayName'] || '',
            username: existingAccount['username'] || ''
          });
          this.panVerified.set(false);
        } else {
          this.existingPanAccount.set(null);
          this.panVerified.set(true);
          
          // Fetch available persons if organization, country, or minor
          const age = this.calculateAge(details.dob);
          if (accType === 'organization' || accType === 'country' || (accType === 'person' && age < 18)) {
            await this.fetchAvailablePersons();
          }
        }
      } catch (dbErr) {
        console.warn('Could not check PAN duplication in database:', dbErr);
        this.existingPanAccount.set(null);
        this.panVerified.set(true);
      }
      
    } catch (err) {
      console.error('PAN extraction error:', err);
      this.notificationService.error('Failed to extract PAN details.');
    } finally {
      this.loadingPan.set(false);
    }
  }

  onUsernameKeypress(event: KeyboardEvent) {
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      const mode = params['mode'];
      const oobCode = params['oobCode'];

      if (mode && oobCode) {
        this.oobCode = oobCode;
        if (typeof window !== 'undefined') {
          this.handleAuthAction(mode, oobCode);
        }
      }
    });

    this.usernameCheck$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(async (username) => {
        if (!username) return null;
        this.usernameStatus.set('checking');
        const accountsRef = collection(db, 'accounts');
        const q = query(accountsRef, where('username', '==', username));
        const snapshot = await getDocs(q);
        return snapshot.empty ? 'available' : 'taken';
      })
    ).subscribe(status => {
      this.usernameStatus.set(status as 'checking' | 'available' | 'taken' | 'invalid' | null);
    });

    this.emailCheck$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(async (email) => {
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'invalid';
        this.emailStatus.set('checking');
        const accountsRef = collection(db, 'accounts');
        const q = query(accountsRef, where('email', '==', email.toLowerCase()));
        const snapshot = await getDocs(q);
        return snapshot.empty ? 'available' : 'taken';
      })
    ).subscribe(status => {
      this.emailStatus.set(status as 'checking' | 'available' | 'taken' | 'invalid' | null);
    });

    this.phoneCheck$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(async (phone) => {
        const digits = (phone || '').replace(/\D/g, '');
        if (digits.length !== 10) return null;
        this.phoneStatus.set('checking');
        const accountsRef = collection(db, 'accounts');
        let q = query(accountsRef, where('phoneNumber', '==', digits));
        let snapshot = await getDocs(q);
        if (snapshot.empty) {
          q = query(accountsRef, where('phoneNumber', '==', '+91' + digits));
          snapshot = await getDocs(q);
        }
        return snapshot.empty ? 'available' : 'taken';
      })
    ).subscribe(status => {
      this.phoneStatus.set(status as 'checking' | 'available' | 'taken' | 'invalid' | null);
    });

    this.identifierCheck$.pipe(
      debounceTime(350),
      distinctUntilChanged(),
      switchMap(async (identifier) => {
        if (!identifier || !identifier.trim()) return null;
        
        const signInEmail = identifier.trim();
        const accountsRef = collection(db, 'accounts');
        let snapshot: any = null;
        
        if (signInEmail.includes('@') && signInEmail.includes('.')) {
          let q = query(accountsRef, where('email', '==', signInEmail.toLowerCase()));
          snapshot = await getDocs(q);
          if (snapshot.empty) {
            q = query(accountsRef, where('email', '==', signInEmail));
            snapshot = await getDocs(q);
          }
        } else {
          const cleanUsername = signInEmail.startsWith('@') ? signInEmail.substring(1) : signInEmail;
          let q = query(accountsRef, where('username', '==', cleanUsername.toLowerCase()));
          snapshot = await getDocs(q);
          if (snapshot.empty) {
            q = query(accountsRef, where('username', '==', cleanUsername));
            snapshot = await getDocs(q);
          }
          
          if (snapshot.empty) {
            const digits = signInEmail.replace(/\D/g, '');
            q = query(accountsRef, where('phoneNumber', '==', signInEmail));
            snapshot = await getDocs(q);
            if (snapshot.empty && digits.length >= 10) {
              const last10 = digits.slice(-10);
              q = query(accountsRef, where('phoneNumber', '==', last10));
              snapshot = await getDocs(q);
              if (snapshot.empty) {
                q = query(accountsRef, where('phoneNumber', '==', '+91' + last10));
                snapshot = await getDocs(q);
              }
            }
          }

          if (snapshot.empty && signInEmail.includes('@')) {
            q = query(accountsRef, where('email', '==', signInEmail.toLowerCase()));
            snapshot = await getDocs(q);
          }
        }
        
        if (snapshot && !snapshot.empty) {
          const doc = snapshot.docs[0];
          const data = doc.data();
          return {
            found: true,
            account: {
              uid: doc.id,
              name: data['displayName'] || data['name'] || '',
              username: data['username'] || '',
              photo: data['photoURL'] || data['photo'] || null,
              email: data['email'] || '',
              accountType: data['accountType'] || 'person'
            }
          };
        }
        return { found: false, account: null };
      })
    ).subscribe(result => {
      if (!result) {
        this.matchedAccount.set(null);
        this.userVerificationStatus.set(null);
      } else if (result.found && result.account) {
        this.matchedAccount.set(result.account);
        if (result.account.accountType === 'person') {
          this.userVerificationStatus.set('verified');
        } else {
          this.userVerificationStatus.set('failed');
        }
      } else {
        this.matchedAccount.set(null);
        this.userVerificationStatus.set('failed');
      }
    });
  }

  clearIdentifier() {
    this.email = '';
    this.userVerificationStatus.set(null);
    this.matchedAccount.set(null);
    this.password.set('');
  }

  onUsernameChange() {
    if (this.mode() === 'signup') {
      // Cascading reset of all subsequent form fields and state
      this.email = '';
      this.emailStatus.set(null);
      this.phoneNumber = '';
      this.phoneStatus.set(null);
      this.password.set('');
    }
    const suffix = this.usernameSuffix();
    if (suffix.length >= 1 && suffix.length <= 4) {
      const fullUsername = this.usernamePrefix() + suffix;
      this.usernameCheck$.next(fullUsername);
    } else {
      this.usernameStatus.set(suffix.length === 0 ? null : 'invalid');
    }
  }

  onEmailChange() {
    if (this.email) {
      this.email = this.email.toLowerCase();
    }
    const val = this.email.trim();
    if (this.mode() === 'signup') {
      // Cascading reset of all subsequent form fields and state
      this.phoneNumber = '';
      this.phoneStatus.set(null);
      this.password.set('');
      this.emailCheck$.next(val);
    } else if (this.mode() === 'signin' || this.mode() === 'forgot') {
      if (!val) {
        this.userVerificationStatus.set(null);
        this.matchedAccount.set(null);
        this.password.set('');
      } else {
        this.userVerificationStatus.set('checking');
        this.identifierCheck$.next(val);
      }
    }
  }

  onPhoneChange() {
    if (this.mode() === 'signup') {
      // Cascading reset of subsequent password field
      this.password.set('');
      const digits = (this.phoneNumber || '').replace(/\D/g, '');
      if (digits.length === 10) {
        this.phoneCheck$.next(digits);
      } else {
        this.phoneStatus.set(digits.length === 0 ? null : 'invalid');
      }
    }
  }

  generateStrongPassword() {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=";
    let password = "";
    for (let i = 0; i < 16; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  }

  async handleAuthAction(mode: string, oobCode: string) {
    this.loading.set(true);
    try {
      const info = await this.authService.getActionCodeInfo(oobCode);
      const operation = info.operation;

      switch (operation) {
        case 'VERIFY_EMAIL':
          this.mode.set('verifyEmail');
          await this.authService.handleVerifyEmail(oobCode);
          this.notificationService.success('Email verified successfully!');
          this.mode.set('actionSuccess');
          break;
        case 'PASSWORD_RESET':
          this.mode.set('resetPassword');
          // We stay in this mode to let user enter new password
          break;
        case 'RECOVER_EMAIL':
          this.mode.set('recoverEmail');
          await this.authService.handleRecoverEmail(oobCode);
          this.notificationService.success('Email recovery successful!');
          this.mode.set('actionSuccess');
          break;
        case 'VERIFY_AND_CHANGE_EMAIL':
          this.mode.set('verifyEmail');
          await this.authService.handleVerifyEmail(oobCode);
          this.notificationService.success('Email changed and verified successfully!');
          this.mode.set('actionSuccess');
          break;
        default:
          this.notificationService.error('Unsupported auth action.');
          this.mode.set('signin');
      }
    } catch (err: unknown) {
      console.error('Auth action error:', err);
      let message = 'Action failed.';
      
      const firebaseError = err as { code?: string; message?: string };
      if (firebaseError.code) {
        switch (firebaseError.code) {
          case 'auth/expired-action-code':
            message = 'The link has expired. Please request a new one.';
            break;
          case 'auth/invalid-action-code':
            message = 'The link is invalid or has already been used.';
            break;
          case 'auth/user-disabled':
            message = 'This account has been disabled.';
            break;
          case 'auth/user-not-found':
            message = 'No account found for this link.';
            break;
          default:
            message = firebaseError.message || message;
        }
      } else if (err instanceof Error) {
        message = err.message;
      }
      
      this.notificationService.error(message);
      this.mode.set('signin');
    } finally {
      this.loading.set(false);
    }
  }

  async createAccount() {
    return this.performSignup();
  }

  async performSignup() {
    this.authService.isSigningUp.set(true);
    const checklist: { id: string; label: string; status: 'pending' | 'loading' | 'success' | 'error'; error?: string }[] = [
      { id: 'pan', label: 'Verifying PAN Card', status: 'pending' },
    ];

    if (this.accountType() === 'person') {
      checklist.push({ id: 'selfie', label: 'Verifying Selfie', status: 'pending' });
    }

    checklist.push(
      { id: 'kicgle_id', label: '17-Digit Kicgle ID Uniqueness', status: 'pending' },
      { id: 'username', label: 'Verifying Username', status: 'pending' },
      { id: 'email', label: 'Verifying Email', status: 'pending' },
      { id: 'phone', label: 'Verifying Phone Number', status: 'pending' },
      { id: 'auth_persons', label: 'Verifying Authorized Persons', status: 'pending' },
      { id: 'create_auth', label: 'Creating Auth Account', status: 'pending' },
      { id: 'upload_files', label: 'Uploading Documents', status: 'pending' },
      { id: 'save_data', label: 'Saving Profile Data', status: 'pending' }
    );

    this.authService.signupChecklist.set(checklist);

    const updateStatus = (id: string, status: 'loading' | 'success' | 'error', error?: string) => {
      this.authService.signupChecklist.update(list => list.map(item => item.id === id ? { ...item, status, error } : item));
    };

    try {
      // 1. Verify PAN (Duplicate check)
      updateStatus('pan', 'loading');
      const accountsRef = collection(db, 'accounts');
      const panQ = query(accountsRef, where('documentNumber', '==', this.panDetails()?.panNumber));
      const panSnap = await getDocs(panQ);
      if (!panSnap.empty) {
        updateStatus('pan', 'error', 'PAN already registered');
        return;
      }
      updateStatus('pan', 'success');

      // 2. Verify Selfie (if person)
      if (this.accountType() === 'person') {
        updateStatus('selfie', 'loading');
        if (!this.selfieImage()) {
          updateStatus('selfie', 'error', 'Selfie required');
          return;
        }
        updateStatus('selfie', 'success');
      }

      // 3. Verify 17-digit Kicgle ID Uniqueness (Collision detection & backend validation check)
      updateStatus('kicgle_id', 'loading');
      if (!this.kicgleId() || this.kicgleId().length !== 17) {
        await this.generateUniqueKicgleId();
      }

      let isIdAvailable = await this.isKicgleIdAvailable(this.kicgleId());
      if (!isIdAvailable) {
        // Execute immediate auto-regeneration loop to produce new ID, verify availability, and repeat
        await this.generateUniqueKicgleId();
        isIdAvailable = await this.isKicgleIdAvailable(this.kicgleId());
      }

      if (!isIdAvailable) {
        updateStatus('kicgle_id', 'error', 'Kicgle ID collision detected');
        return;
      }
      updateStatus('kicgle_id', 'success');

      // 4. Verify Username
      updateStatus('username', 'loading');
      const fullUsername = this.usernamePrefix() + this.usernameSuffix();
      const userQ = query(accountsRef, where('username', '==', fullUsername));
      const userSnap = await getDocs(userQ);
      if (!userSnap.empty) {
        updateStatus('username', 'error', 'Username taken');
        return;
      }
      updateStatus('username', 'success');

      // 5. Verify Email
      updateStatus('email', 'loading');
      const emailQ = query(accountsRef, where('email', '==', this.email.toLowerCase()));
      const emailSnap = await getDocs(emailQ);
      if (!emailSnap.empty) {
        updateStatus('email', 'error', 'Email already in use');
        return;
      }
      updateStatus('email', 'success');

      // 6. Verify Phone Number
      updateStatus('phone', 'loading');
      const digits = this.phoneNumber.replace(/\D/g, '');
      let phoneQ = query(accountsRef, where('phoneNumber', '==', digits));
      let phoneSnap = await getDocs(phoneQ);
      if (phoneSnap.empty) {
        phoneQ = query(accountsRef, where('phoneNumber', '==', '+91' + digits));
        phoneSnap = await getDocs(phoneQ);
      }
      if (!phoneSnap.empty) {
        updateStatus('phone', 'error', 'Phone already in use');
        return;
      }
      updateStatus('phone', 'success');

      // 7. Verify Authorized Persons
      updateStatus('auth_persons', 'loading');
      if (!this.isAuthorizedPersonsValid()) {
        updateStatus('auth_persons', 'error', 'Invalid authorized persons');
        return;
      }
      updateStatus('auth_persons', 'success');

      // 8. Create Auth Account
      updateStatus('create_auth', 'loading');
      const signupPassword = (this.accountType() === 'organization' || this.accountType() === 'country') ? this.generateStrongPassword() : this.password();
      const userCredential = await this.authService.signUp(this.email, signupPassword);
      const uid = userCredential.user.uid;
      updateStatus('create_auth', 'success');

      // 9. Upload Documents
      updateStatus('upload_files', 'loading');
      let panUrl = '';
      let selfieUrl = '';

      if (this.panImage()) {
        const panRef = ref(storage, `accounts/${uid}/pan`);
        await uploadString(panRef, this.panImage()!, 'data_url');
        panUrl = await getDownloadURL(panRef);
      }

      if (this.selfieImage()) {
        const selfieRef = ref(storage, `accounts/${uid}/selfie`);
        await uploadString(selfieRef, this.selfieImage()!, 'data_url');
        selfieUrl = await getDownloadURL(selfieRef);
      }
      updateStatus('upload_files', 'success');

      // 10. Save Profile Data
      updateStatus('save_data', 'loading');
      const age = this.calculateAge(this.panDetails()?.dob || '');
      const isMinor = this.accountType() === 'person' && age < 18;
      const extractedDob = this.panDetails()?.dob ? this.parseDob(this.panDetails()!.dob) : null;
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const profileData: any = {
        uid,
        email: this.email.toLowerCase(),
        phoneNumber: this.phoneNumber,
        displayName: this.panDetails()?.name,
        documentNumber: this.panDetails()?.panNumber,
        accountType: this.accountType(),
        username: fullUsername,
        photoURL: selfieUrl || `https://ui-avatars.com/api/?name=${this.email}&background=007AFF&color=fff`,
        documentUrl: panUrl,
        selfieUrl,
        dob: extractedDob,
        status: AccountStatus.ACTIVE,
        kicgle: this.kicgleId(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, 'accounts', uid), profileData);

      // Create role if minor, organization, or country
      if (isMinor || this.accountType() === 'organization' || this.accountType() === 'country') {
        for (const person of this.selectedAuthorizedPersons()) {
          const roleRef = doc(collection(db, 'accounts', uid, 'roles'));
          await setDoc(roleRef, {
            uid: person.uid || doc(collection(db, 'accounts')).id,
            role: uid,
            roles: [person.role],
            startDate: serverTimestamp(),
            start: serverTimestamp(),
            startedBy: uid
          });
        }
      }

      // Save KYC data
      const kycRef = doc(collection(db, 'accounts', uid, 'kyc'));
      await setDoc(kycRef, {
        uid,
        kyc: this.accountType(),
        documentType: 'pan',
        documentNumber: this.panDetails()?.panNumber,
        documentUrl: panUrl ? [panUrl] : [],
        selfie: selfieUrl,
        photo: selfieUrl,
        name: this.panDetails()?.name,
        dob: this.panDetails()?.dob ? new Date(this.panDetails()!.dob.split('/').reverse().join('-')) : null,
        status: 'verified',
        kicgle: this.kicgleId(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      updateStatus('save_data', 'success');

      // Create default user settings in accounts/{uid}/settings/default
      const defaultSettingsRef = doc(db, 'accounts', uid, 'settings', 'default');
      await setDoc(defaultSettingsRef, {
        Theme: 'light',
        Background: 'India',
        Notification: 'enabled',
        Language: 'English',
        theme: 'light',
        background: 'India',
        notification: 'enabled',
        language: 'English',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      this.notificationService.success('Account created successfully!');
      
      if (this.accountType() === 'organization' || this.accountType() === 'country') {
        await this.authService.signOut();
        this.notificationService.info(`${this.accountType() === 'country' ? 'Country' : 'Organization'} account created. Please wait for verification.`);
        this.authService.isSigningUp.set(false);
        this.mode.set('signin');
      } else {
        this.router.navigate(['/search']);
      }

    } catch (err: unknown) {
      console.error('Signup process error:', err);
      const activeItem = this.authService.signupChecklist().find(item => item.status === 'loading');
      const errorMessage = err instanceof Error ? err.message : 'Process failed';
      if (activeItem) {
        updateStatus(activeItem.id, 'error', errorMessage);
      } else {
        this.notificationService.error('Signup failed: ' + errorMessage);
        this.authService.isSigningUp.set(false);
      }
    }
  }

  resetAllFields() {
    this.email = '';
    this.phoneNumber = '';
    this.password.set('');
    this.showPassword.set(false);
    this.loading.set(false);
    this.panImage.set(null);
    this.panDetails.set(null);
    this.usernamePrefix.set('');
    this.usernameSuffix.set('');
    this.accountType.set('');
    this.loadingPan.set(false);
    this.existingPanAccount.set(null);
    this.panVerified.set(false);
    this.selfieImage.set(null);
    this.isFaceVerified.set(false);
    this.kicgleId.set('');
    this.capturedPhotoTemp.set(null);
    this.isValidatingPhoto.set(false);
    this.selfieValidationError.set(null);
    this.isCameraActive.set(false);
    this.stopCamera();
    this.stopPanCamera();
    this.panInputMethod.set('upload');
    this.panCameraError.set(null);
    this.panCameraLoading.set(false);
    this.usernameStatus.set(null);
    this.emailStatus.set(null);
    this.phoneStatus.set(null);
    this.userVerificationStatus.set(null);
    this.matchedAccount.set(null);
    this.selectedAuthorizedPersons.set([]);
    this.personSearchTerm.set('');
    this.newAuthPersonUid = '';
    this.newAuthPersonName = '';
    this.newAuthPersonUsername = '';
    this.newAuthPersonPhoto = null;
    this.newAuthPersonRole = '';
  }

  onLogoClick() {
    this.resetAllFields();
    this.mode.set('signin');
    this.router.navigate([], { queryParams: {} });
  }

  toggleMode() {
    this.resetAllFields();
    if (this.mode() === 'signin') this.mode.set('signup');
    else this.mode.set('signin');
  }

  togglePassword() {
    this.showPassword.update(v => !v);
  }

  async onSubmit() {
    if (this.mode() !== 'resetPassword' && !this.email) {
      this.notificationService.error('Please enter your email, phone, or username.');
      return;
    }
    if (this.mode() === 'signup' && !this.phoneNumber) {
      this.notificationService.error('Phone number is required for signup.');
      return;
    }
    if (this.mode() !== 'forgot' && this.mode() !== 'signup' && !this.password()) {
      this.notificationService.error('Please enter your password.');
      return;
    }
    // For signup, if it's a person, password is required. If organization, it's auto-generated.
    if (this.mode() === 'signup' && this.accountType() === 'person' && !this.password()) {
      this.notificationService.error('Please enter a password.');
      return;
    }

    this.loading.set(true);

    try {
      if (this.mode() === 'signin' || this.mode() === 'forgot') {
        let targetEmail = this.email.trim();
        let accountType = 'person';
        
        if (this.matchedAccount()?.email) {
          targetEmail = this.matchedAccount()!.email;
          accountType = this.matchedAccount()!.accountType || 'person';
        } else if (!targetEmail.includes('@')) {
          const accountsRef = collection(db, 'accounts');
          
          // Clean username (remove @ if present)
          const cleanUsername = targetEmail.startsWith('@') ? targetEmail.substring(1) : targetEmail;
          
          // Check by username
          let q = query(accountsRef, where('username', '==', cleanUsername));
          let snapshot = await getDocs(q);
          
          if (snapshot.empty) {
            // Check by phone number
            q = query(accountsRef, where('phoneNumber', '==', targetEmail));
            snapshot = await getDocs(q);
          }
          
          if (!snapshot.empty) {
            const userData = snapshot.docs[0].data();
            accountType = userData['accountType'] || 'person';
            if (userData['email']) {
              targetEmail = userData['email'];
            } else {
              this.notificationService.error('Account found, but no email is associated with it.');
              this.loading.set(false);
              return;
            }
          } else {
            this.notificationService.error('No account found with this username or phone number.');
            this.loading.set(false);
            return;
          }
        } else {
          // It's an email, we still need to check if it's an organization account
          const accountsRef = collection(db, 'accounts');
          const q = query(accountsRef, where('email', '==', targetEmail));
          const snapshot = await getDocs(q);
          if (!snapshot.empty) {
            accountType = snapshot.docs[0].data()['accountType'] || 'person';
          }
        }

        if (accountType !== 'person') {
          this.notificationService.error('Sign in and password reset are only allowed for person accounts.');
          this.loading.set(false);
          return;
        }

        if (this.mode() === 'signin') {
          await this.authService.signIn(targetEmail, this.password());
          this.notificationService.success('Welcome back!');
          this.router.navigate(['/search']);
        } else if (this.mode() === 'forgot') {
          await this.authService.resetPassword(targetEmail);
          this.notificationService.info('Check your email for password reset instructions.');
        }
      } else if (this.mode() === 'signup') {
        await this.createAccount();
      } else if (this.mode() === 'resetPassword' && this.oobCode) {
        await this.authService.handleResetPassword(this.oobCode, this.password());
        this.notificationService.success('Password updated successfully!');
        this.mode.set('actionSuccess');
      }
    } catch (err: unknown) {
      console.error('Auth error:', err);
      let message = 'An error occurred. Please try again.';
      
      const firebaseError = err as { code?: string; message?: string };
      if (firebaseError.code) {
        switch (firebaseError.code) {
          case 'auth/email-already-in-use':
            message = 'This email is already registered. Try signing in instead.';
            break;
          case 'auth/invalid-email':
            message = 'Please enter a valid email address.';
            break;
          case 'auth/user-disabled':
            message = 'This account has been disabled.';
            break;
          case 'auth/invalid-credential':
          case 'auth/invalid-login-credentials':
          case 'auth/user-not-found':
          case 'auth/wrong-password':
            message = 'Invalid email, username, or password. Please check your details and try again.';
            break;
          case 'auth/weak-password':
            message = 'Password is too weak. Please use at least 6 characters.';
            break;
          case 'auth/operation-not-allowed':
            message = 'This sign-in method is not enabled.';
            break;
          case 'auth/too-many-requests':
            message = 'Too many failed attempts. Please try again later.';
            break;
          default:
            message = firebaseError.message || message;
        }
      } else if (err instanceof Error) {
        message = err.message;
      }
      
      this.notificationService.error(message);
    } finally {
      this.loading.set(false);
    }
  }
}
